/**
 * EditFormV1 — Generic reusable edit form component
 *
 * Works for any module (jobs, candidates, submissions, etc.)
 * All module-specific behaviour is passed in via props — zero hardcoding inside.
 *
 * Props:
 *   moduleName      string    — e.g. "job", "candidate"
 *   groupName       string    — optional; render/fetch/submit only the single
 *                              named group from moduleName's form config (e.g.
 *                              a module's own "actuals"/update-payment group)
 *                              instead of every group the module has
 *   recordId        string    — MongoDB _id of the record
 *   breadcrumbItems array     — passed to AppBreadcrumb
 *   submitText      string    — Save button label (default "Save Changes")
 *   cancelPath      string    — navigate here on cancel/success
 *   onSuccess       function  — called after successful save (overrides cancelPath)
 *   fieldNameMap    object    — rename form fields before sending to API
 *                              e.g. { mspReqId: 'mspreqId' }
 *   onFileUpload    function  — (fieldName, file, recordId) => Promise
 *                              handle multipart uploads per module
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Button,
    Card,
    Checkbox,
    Col,
    DatePicker,
    Form,
    Input,
    Radio,
    Row,
    Select,
    Space,
    Spin,
    Switch,
    TimePicker,
    Tooltip,
    Typography,
    Upload,
    message,
} from 'antd';
import {
    CalendarOutlined,
    CloudUploadOutlined,
    DeleteOutlined,
    DownOutlined,
    EnvironmentOutlined,
    EyeOutlined,
    FileExcelOutlined,
    FileImageOutlined,
    FileOutlined,
    FilePdfOutlined,
    FileTextOutlined,
    FileWordOutlined,
    InfoCircleOutlined,
    LockOutlined,
    MailOutlined,
    PhoneOutlined,
    PlusOutlined,
    SolutionOutlined,
    TagsOutlined,
    UpOutlined,
    UserOutlined,
} from '@ant-design/icons';
import '../styles/AddFormV1.css';
import AppBreadcrumb from './Breadcrumb';
import TipTapEditor from './TipTapEditor';
import DocumentViewer from './DocumentViewer';
import { applyInputValidator, getKeyPattern } from './form/inputValidator';
import { configuredEmailRule } from './form/emailValidator';
import { securityValidationRule } from './form/inputSecurity';
import {
    buildUniqueValidationRule,
    attachUniqueSyncGuards,
    collectUniqueRules,
    createUniqueChecker,
    extractDuplicateFieldErrors,
} from './form/uniqueFieldValidation';
import { checkFieldUnique } from '../services/uniqueValidationApi';
import { uploadAcceptCategory } from './form/uploadAccept';
import { useQuickField } from './form/QuickCreateEditField';
import { maxFromFieldRule, maxFromFieldDependency, resolveScopedFieldPath } from './form/crossFieldRules';
import { checkMaxWithFallback, parseCompareFields, readCompareValue, resolveCompareFieldName } from './form/maxCeiling';
import { prefillWhenWatchFields, prefillWhenWatchPaths, resolvePrefillWhen } from './form/prefillWhenRules';
import { narrowOptionsByConstraint, stripExclusivePrefillOptions } from './form/optionConstraints';
import { filterLookupOptionRows, lookupExtraFieldKeys } from './form/optionRowFilters';
import { fieldTooltipText } from './form/fieldTooltip';
import { roleAllowsAction } from '../utils/roleAllowsAction';
import { groupAllowedByRole, fieldAllowedByRole, fieldEditableByRole } from '../utils/roleFormPermissionGate';
import { resolveAfterSubmit, safeNavTarget } from '../utils/afterSubmitNav';
import { goBackOrTo } from '../utils/backNav';
import {
    resolveRegionRules,
    inProgressAllowed,
    inProgressBlocks,
    inProgressError,
    buildInProgressValidator,
    promptRowMove,
} from './form/educationRules';
import { openFormNotice, openFormDecision } from './form/formDecisionDialog';
import ValidationOverrideCheckbox from './form/ValidationOverrideCheckbox';
import {
    overrideConfig,
    overrideFlagPath,
    overrideRuleMapper,
} from './form/validationOverride';
import { getDefaults } from '../services/detailDefaults';
import { applyGroupClear } from './form/clearGroupOnChange';
import { fetchLookupLabels, mergeResolvedOptions, missingOptionValues } from './form/resolveStoredOptions';
import {
    buildPairValidator,
    buildRowHierarchyValidator,
    buildDisabledDate,
    defaultPickerValue,
    getComparableDateTime,
    getComparableTimeOfDay,
} from './form/dateRules';
import { submitSuccessMessage, submitErrorMessage } from '../utils/submitMessages';
import { scrollToFirstFormError } from '../utils/scrollToFirstFormError';
import { conditionFieldPath, configuredFieldLabel, conditionMatches as labelConditionMatches, colWhenRules, resolveColSpan } from './form/conditionalFieldLabel';
import { useNavigate, useLocation } from 'react-router-dom';
import { clearAuthTokenOverride, fetchJsonWithAuth, setAuthTokenOverride } from '../services/authApi';
import { AUTH_URL } from '../services/apiConfig';
import { buildFileUrl } from './detail/renderConfig';
import { getDropdownValues } from '../services/moduleDataApi';
import { getFormGroups } from '../services/addFormApi';
import { updateRecord } from '../services/detailedViewApi';
import { getModuleDocuments, uploadModuleDocuments } from '../services/documentApi';
import { loadDetailDefaults, formatPhone, phoneDigits, parsePhoneFormat } from '../services/detailDefaults';
import { getRoleFormPermissions, getModuleDefaultFields } from '../services/adminApi';
import {
    buildPayload as buildConfigPayload,
    buildRepeatFieldInitial,
    singleFieldOf,
    collectFileParts,
    isUploadField,
    effectiveDataType,
    getDeep,
    toSelectControlValue,
    usesLabeledSelectValue,
    rowDefaultsFor,
} from './form/payloadTransformer';
import { computeLinkedSets, findLinkedSet, padLinkedInitialValues } from './form/linkedAddRowGroups';
import { getEditFormFallbackOptions } from './form/editFormOptionFallbacks';
import useAiActions, { groupActionsByPosition } from './form/useAiActions';
import AiActionButtonGroup from './form/AiActionButtons';
import { namePathFromString, buildAddRowInitial, applyScalarFieldValues, writeFormValue } from './form/applyGroupValues';
import {
    contextPrefillModules,
    contextRecordId,
    extractContextGroups,
    flattenContextRecord,
    isNonPersistentCarrier,
    pickContextValue,
} from './form/contextPrefill';
import { verifyEmail } from '../services/adminApi';
import { parseLockFieldKeys, isVerifyLocked, readAtPath } from './form/verifyLockState';

const { Text } = Typography;
const { TextArea } = Input;

function siblingFieldPath(absoluteName, key) {
    return Array.isArray(absoluteName) && absoluteName.length > 1
        ? [...absoluteName.slice(0, -1), key]
        : [key];
}

// VerifyFieldButton — mirrors AddFormV1.jsx's identical component.
function VerifyFieldButton({ form, field, absoluteName }) {
    const [checking, setChecking] = useState(false);
    const [verdict, setVerdict] = useState(null);
    const verifiedValueRef = useRef(null);
    const resultValue = Form.useWatch(
        field.verifyResultField ? siblingFieldPath(absoluteName, field.verifyResultField) : ['__noop_verify__'],
        { form, preserve: true },
    );
    const currentValue = Form.useWatch(absoluteName, form);
    // Locked "verified" state (EDIT ONLY — see the note on AddFormV1's copy).
    // `verifyLockedWhenFields` names sibling row keys whose truthiness means
    // something irreversible already happened for this value (a login was
    // provisioned for the address), so the control must stop offering a
    // re-check. One selector-form useWatch, not one per key: the key list is
    // admin config and its LENGTH must never decide how many hooks run.
    const lockKeys = useMemo(
        () => parseLockFieldKeys(field.verifyLockedWhenFields),
        [field.verifyLockedWhenFields],
    );
    const locked = Form.useWatch(
        (values) => isVerifyLocked({
            lockKeys,
            resultValue: readAtPath(values, siblingFieldPath(absoluteName, field.verifyResultField)),
            hasResultField: Boolean(field.verifyResultField),
            readSibling: (key) => readAtPath(values, siblingFieldPath(absoluteName, key)),
        }),
        { form, preserve: true },
    );

    // Editing the value of a row that is LOCKED (an account already exists for
    // the stored address) deliberately still runs this reset. It clears ONLY
    // the two verify carriers — never the lock fields — which is the safe
    // split:
    //   • the lock/provisioning stamps (isAccountCreate, accountUserId, …)
    //     describe an account that really was created and still exists, so
    //     wiping them would make the record lie about the outside world;
    //   • the validity stamp described the OLD address, so keeping it would
    //     badge a freshly typed, never-checked address as "Verified".
    // Because the lock requires the result field too, clearing the result
    // UNLOCKS the row: the button comes back as "Re Verify", the group's
    // requireValidatedField gate blocks the save until the new address passes,
    // and re-verifying re-locks it. Suppressing the reset instead would strand
    // the row in a Verified state with no way to re-check it.
    useEffect(() => {
        const normalized = String(currentValue ?? '').trim();
        if (resultValue === true && verifiedValueRef.current === null && normalized) {
            verifiedValueRef.current = normalized;
            return;
        }
        if (resultValue === true && verifiedValueRef.current !== null && normalized !== verifiedValueRef.current) {
            if (field.verifyResultField) form.setFieldValue(siblingFieldPath(absoluteName, field.verifyResultField), false);
            if (field.verifyTimestampField) form.setFieldValue(siblingFieldPath(absoluteName, field.verifyTimestampField), undefined);
            verifiedValueRef.current = null;
        }
    }, [absoluteName, currentValue, field.verifyResultField, field.verifyTimestampField, form, resultValue]);

    if (field.verifyAction !== 'email') return null;

    const runVerify = async () => {
        const email = String(currentValue ?? '').trim();
        if (!email) {
            message.warning('Enter an email first');
            return;
        }
        setChecking(true);
        try {
            const result = await verifyEmail(email);
            verifiedValueRef.current = result.valid ? email : null;
            setVerdict(result.valid ? result : null);
            if (field.verifyResultField) form.setFieldValue(siblingFieldPath(absoluteName, field.verifyResultField), result.valid);
            if (field.verifyTimestampField) {
                form.setFieldValue(siblingFieldPath(absoluteName, field.verifyTimestampField), result.valid ? new Date().toISOString() : undefined);
            }
            // Three outcomes, not two. "Accepted but unconfirmed" is the common
            // case on catch-all domains (Google Workspace's default, and most
            // corporate mail) — reporting it as a plain success would tell the
            // user the mailbox exists when nothing proved that.
            if (!result.valid) {
                message.error(result.reason ? `Invalid email: ${result.reason}` : 'Email could not be verified');
            } else if (result.mailboxConfirmed) {
                message.success('Email verified \u2014 the mail server confirmed this mailbox');
            } else {
                message.warning(result.reason || 'The domain can receive mail, but this exact mailbox could not be confirmed');
            }
        } catch (err) {
            verifiedValueRef.current = null;
            setVerdict(null);
            if (field.verifyResultField) form.setFieldValue(siblingFieldPath(absoluteName, field.verifyResultField), false);
            if (field.verifyTimestampField) form.setFieldValue(siblingFieldPath(absoluteName, field.verifyTimestampField), undefined);
            message.error(err?.message || 'Could not verify email');
        } finally {
            setChecking(false);
        }
    };

    if (locked) {
        // Non-interactive on purpose: there is no button and no onClick, so the
        // check cannot be re-run. Wins over the "Valid"/"Accepted" labels below
        // because those describe a re-runnable check, and this one isn't.
        return (
            <Text type="success" strong className="v1-verify-field-wrap">
                {field.verifyLockedLabel || 'Verified'}
            </Text>
        );
    }

    if (resultValue === true) {
        // Accepted, but the mailbox itself was never proven (catch-all domain).
        // Labelling that "Valid" would overstate what the check established, so
        // it gets its own state. The address still passes the submit gate — the
        // account email that follows is what ultimately proves delivery.
        // Only positive evidence of "unconfirmed" downgrades the label. A
        // response that simply doesn't carry the field (older backend, or a
        // check that never reached the mailbox stage) keeps the plain success
        // state rather than casting doubt the check never actually raised.
        if (verdict && !verdict.mailboxConfirmed && (verdict.catchAll || verdict.result === 'risky')) {
            return (
                <Tooltip title={verdict.reason || 'This domain accepts every address, so the mailbox could not be confirmed. Delivery is proven only once the account email is actually sent.'}>
                    <Text type="warning" strong className="v1-verify-field-wrap">
                        {field.verifyUnconfirmedLabel || 'Accepted'}
                    </Text>
                </Tooltip>
            );
        }
        return <Text type="success" strong className="v1-verify-field-wrap">{field.verifySuccessLabel || 'Valid'}</Text>;
    }

    return (
        <Button size="small" loading={checking} onClick={runVerify} className="v1-verify-field-wrap">
            {resultValue === false ? (field.verifyRetryLabel || 'Re Verify') : (field.verifyIdleLabel || 'Verify Email')}
        </Button>
    );
}

function ConfiguredFieldLabel({ form, field, siblingPrefix, locked, combined = false }) {
    const watchedValue = Form.useWatch(conditionFieldPath(field?.labelWhen, siblingPrefix), form);
    const label = configuredFieldLabel(field, watchedValue, combined);
    return combined ? label : <FieldLabel field={field} label={label} locked={locked} />;
}

function ValidationGatedSubmitButton({ form, groups, disabled, loading, onClick, children }) {
    const unvalidated = Form.useWatch(
        (values) => groups.some((group) => {
            if (!group.addRow || !group.requireValidatedField) return false;
            const rows = values?.[group.name] ?? [];
            return rows.length === 0 || rows.some((row) => !row?.[group.requireValidatedField]);
        }),
        { form, preserve: true },
    );
    const isDisabled = disabled || unvalidated;
    // Config-driven disabled-reason tooltip (mirrors AddFormV1): admin-set
    // group.submitDisabledMessage, or a row-validation-specific default.
    const groupMsg = groups.find((g) => g.submitDisabledMessage)?.submitDisabledMessage;
    const validatedMsg = groups.find((g) => g.requireValidatedField)?.requireValidatedFieldMessage;
    const reason = unvalidated
        ? (validatedMsg || groupMsg || 'Verify every required row before submitting.')
        : (groupMsg || 'Complete all mandatory fields before submitting.');
    const button = (
        <Button type="primary" onClick={onClick} disabled={isDisabled} loading={loading}>
            {children}
        </Button>
    );
    return isDisabled && !loading
        ? <Tooltip title={reason}><span style={{ display: 'inline-flex', cursor: 'not-allowed' }}>{button}</span></Tooltip>
        : button;
}

// PrefillWhenWatcher — mirrors AddFormV1.jsx's identical component: sets this
// field's own value when a referenced field's value matches a configured rule,
// clears it when a rule is marked `clearValue`, and (resetOnNoMatch, admin
// config `prefillWhenReset`, default on) clears it back out when the watched
// value stops matching ANY rule — but only when the field still holds exactly
// what a previous match set, never a value the user/record genuinely carries.
//
// Both forms delegate every decision to form/prefillWhenRules.js, so edit and
// add can never drift. It IS safe to run the clear paths on edit: a clear only
// ever fires after the user changes the watched field themselves (the pure
// resolver suppresses clears on the first evaluation precisely so opening a
// record whose stored contractType already matches a clear rule cannot delete
// the stored employer), and the mismatch clear is additionally gated on the
// field still holding the exact value a rule wrote in this session.
function PrefillWhenWatcher({ form, rules, watchPaths, targetPath, resetOnNoMatch = true }) {
    const watchedKey = Form.useWatch(
        (values) => JSON.stringify(
            watchPaths.map(({ path }) => path.reduce((acc, key) => (acc == null ? acc : acc[key]), values) ?? null),
        ),
        { form, preserve: true },
    );
    const lastAppliedRef = useRef(undefined);
    const initialRef = useRef(true);
    useEffect(() => {
        const values = watchedKey ? JSON.parse(watchedKey) : [];
        const watched = {};
        watchPaths.forEach(({ field }, i) => { watched[field] = values[i]; });
        const initial = initialRef.current;
        initialRef.current = false;
        const outcome = resolvePrefillWhen({
            rules,
            watched,
            current: form.getFieldValue(targetPath),
            lastApplied: lastAppliedRef.current,
            clearOnMismatch: resetOnNoMatch,
            initial,
        });
        if (outcome.action === 'set') {
            form.setFieldValue(targetPath, outcome.value);
            lastAppliedRef.current = outcome.value;
        } else if (outcome.action === 'clear') {
            form.setFieldValue(targetPath, undefined);
            lastAppliedRef.current = undefined;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [watchedKey]);
    return null;
}


const sectionIcons = [
    <UserOutlined />,
    <EnvironmentOutlined />,
    <SolutionOutlined />,
    <InfoCircleOutlined />,
    <FileTextOutlined />,
];

const fieldIcons = {
    email: <MailOutlined className="v1-input-icon" />,
    contactNumber: <PhoneOutlined className="v1-input-icon" />,
    phone: <PhoneOutlined className="v1-input-icon" />,
    currentLocation: <EnvironmentOutlined className="v1-input-icon" />,
    preferredLocation: <EnvironmentOutlined className="v1-input-icon" />,
    designation: <SolutionOutlined className="v1-input-icon" />,
    skills: <TagsOutlined className="v1-input-icon" />,
    firstName: <UserOutlined className="v1-input-icon" />,
    lastName: <UserOutlined className="v1-input-icon" />,
    jobTitle: <SolutionOutlined className="v1-input-icon" />,
    jobLocation: <EnvironmentOutlined className="v1-input-icon" />,
    vendorEmail: <MailOutlined className="v1-input-icon" />,
};

// ─── Pure helpers ────────────────────────────────────────────────────────────

const truthy = (v) => v === true || v === 1 || v === '1';
const editable = (field, moduleName) => (field?.edit === undefined || truthy(field.edit))
    && (!field?.editablePermission || roleAllowsAction(field.editablePermission, moduleName));
const empty = (v) => v === undefined || v === null || v === '';

function hasMeaningfulValue(value) {
    if (value === undefined || value === null || value === '') return false;
    if (Array.isArray(value)) return value.length > 0 && value.some(hasMeaningfulValue);
    if (typeof value === 'object') return Object.values(value).some(hasMeaningfulValue);
    return true;
}

function normalizeModule(name = '') {
    const n = String(name).toLowerCase();
    if (n === 'job') return 'jobs';
    if (n === 'candidate') return 'candidates';
    if (n === 'submissions') return 'submission';
    return n;
}

function normalizeOptions(options = []) {
    return options.map((o) => {
        if (typeof o === 'string') return { label: o, value: o };
        return {
            label: o.label ?? o.name ?? o.value ?? o.userName ?? o.email,
            value: o.value ?? o.id ?? o.userId ?? o._id ?? o.name ?? o.label,
        };
    });
}

// Group-level showIf — mirrors AddFormV1.jsx so both forms evaluate a group's
// visibility condition identically (same operators as field-level showIf).
function groupShowIfMatches(showIf, value) {
    if (!showIf?.field) return true;
    const actual = String(value ?? '');
    const expected = String(showIf.value ?? '');
    const list = () => expected.split(',').map((v) => v.trim());
    switch (showIf.operator) {
        case 'eq': return actual === expected;
        case 'neq': return actual !== expected;
        case 'in': return list().includes(actual);
        case 'notIn': return !list().includes(actual);
        case 'truthy': return truthy(value);
        case 'falsy': return !truthy(value);
        case 'empty':
        case 'isEmpty':
            return value === undefined || value === null || actual.trim() === '' || (Array.isArray(value) && value.length === 0);
        case 'notEmpty':
            return !(value === undefined || value === null || actual.trim() === '' || (Array.isArray(value) && value.length === 0));
        default: return true;
    }
}

// Group-level disable (mirrors AddFormV1): the whole card goes read-only when
// the group's `disabled` flag is set or its `disabledIf` condition matches.
// Provided via context so every FieldControl in the group inherits it; nested/
// merged groups inherit the parent's disabled state.
const GroupDisabledContext = React.createContext(false);

function GroupDisabledProvider({ form, group, children, inheritParent = true }) {
    const parentDisabled = React.useContext(GroupDisabledContext);
    const path = group?.disabledIf?.field ? namePathFromString(group.disabledIf.field) : ['__noop_group_disabled__'];
    const watched = Form.useWatch(path, form);
    const conditionDisabled = group?.disabledIf?.field
        ? groupShowIfMatches(group.disabledIf, watched)
        : false;
    // `inheritParent: false` is used for a MERGED child (group.mergeInto).
    // mergeInto is presentation only — the child borrows the host's card and
    // nothing else — so inheriting the host's disabled state would give a
    // display relationship real behaviour.
    //
    // It is also the difference between working and deadlocked: the Employer
    // section is disabled until a Contract Type is chosen, and Contract Type is
    // rendered INSIDE that section. Inheriting would disable the very control
    // that enables the card, with no way out.
    const disabled = (inheritParent && parentDisabled)
        || Boolean(group?.disabled)
        || conditionDisabled;
    return <GroupDisabledContext.Provider value={disabled}>{children}</GroupDisabledContext.Provider>;
}

function GroupShowIfGate({ form, showIf, render }) {
    const watched = Form.useWatch(showIf.field, form);
    if (!groupShowIfMatches(showIf, watched)) return null;
    return render();
}

function getNestedProp(obj, dotPath) {
    return String(dotPath ?? '').split('.').reduce((cur, k) => cur?.[k], obj);
}

function FieldLabel({ field, label, locked }) {
    if (!label) return label;
    // field.tooltip (admin config) OR the older infoEnabled/infoText pair —
    // resolved by the same helper AddFormV1 uses, so a field configured once
    // shows the same help text on the add form and the edit form.
    const infoText = fieldTooltipText(field);
    const showInfo = Boolean(infoText);
    if (!showInfo && !locked) return label;
    return (
        <span className="v1-field-label-with-info">
            <span>{label}</span>
            {showInfo && (
                <Tooltip title={infoText}>
                    <InfoCircleOutlined className="v1-field-info-icon" />
                </Tooltip>
            )}
            {locked && (
                <Tooltip title="You don't have permission to edit this field">
                    <LockOutlined className="v1-field-info-icon" />
                </Tooltip>
            )}
        </span>
    );
}

function GroupInfoMessage({ group }) {
    if (!truthy(group?.infoEnabled) || empty(group?.infoText)) return null;
    return <Text type="secondary" className="v1-group-info-message">{group.infoText}</Text>;
}

function GroupToggleControl({ group }) {
    if (!truthy(group?.toggleEnabled) || empty(group?.toggleField)) return null;
    return (
        <Space size={6} align="center" onClick={(event) => event.stopPropagation()}>
            <Text type="secondary" className="v1-combine-toggle-label">{group.toggleLabel || 'Toggle'}</Text>
            <Form.Item name={group.toggleField} valuePropName="checked" initialValue={Boolean(group.toggleValue)} noStyle>
                <Switch size="small" />
            </Form.Item>
        </Space>
    );
}

function normalizeGroups(groups = [], moduleName, rolePerms, defaultFieldSet) {
    const dset = defaultFieldSet ?? new Set();
    return [...groups]
        // hideGroup hides everywhere; hideGroupInForm is the form-only mirror of
        // DetailViewV1's hideGroupInDetail — for server-owned groups that must be
        // read on the detail view but never typed here.
        .filter((g) => !truthy(g.hideGroup) && !truthy(g.hideGroupInForm)
            && groupAllowedByRole(rolePerms, g.name))
        .sort((a, b) => Number(a.order ?? 0) - Number(b.order ?? 0))
        .map((g) => ({
            ...g,
            fields: [...(g.fields ?? [])]
                // Default Fields (Form Groups → Default Fields): always shown +
                // required, so force show/req before the hidden-field filter.
                .map((f) => (dset.has(f.field) ? { ...f, show: 1, req: 1 } : f))
                // A field with a blank key can't render or submit — drop it so
                // one stray admin-config row can't break the whole group. Also
                // drop any field this role's Form Configuration (Role Configure
                // → Form, derived from this same Form Groups config) turned off.
                //
                // A field with show:0 ("hidden") is intentionally KEPT here even
                // though it must never render — this same fields array also feeds
                // buildPayload/buildRowObject (the payload engine has no other way
                // to know a hidden carrier field like isValid/isAccountCreate
                // exists). Dropping it here silently threw away every hidden
                // field's value at submit time. See renderFields, which is what
                // actually keeps hidden fields off the screen.
                .filter((f) => String(f.field ?? '').trim() !== ''
                    && (!f.visiblePermission || roleAllowsAction(f.visiblePermission, moduleName))
                    && fieldAllowedByRole(rolePerms, g.name, f.field))
                // Role "disable": a non-editable group/field renders read-only.
                .map((f) => (
                    fieldEditableByRole(rolePerms, g.name, f.field)
                        ? f
                        : { ...f, disabled: true }
                ))
                .sort((a, b) => Number(a.order ?? 0) - Number(b.order ?? 0)),
        }))
        .filter((g) => g.fields.length);
}

function stripHtml(html) {
    return String(html ?? '')
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .trim();
}

function getValidationValue(field, type) {
    const rules = field.validations ?? field.validation ?? field.rules ?? [];
    const rule = rules.find((r) => (typeof r === 'string' ? r : r?.type) === type);
    if (!rule || typeof rule === 'string') return undefined;
    return rule.value ?? rule.length ?? rule[type];
}

const numberRangeRule = ({ label, message: msg, value, compare, text }) => ({
    validator: (_, input) => {
        if (empty(input)) return Promise.resolve();
        return compare(Number(input), Number(value))
            ? Promise.resolve()
            : Promise.reject(new Error(msg ?? `${label} ${text} ${value}`));
    },
});

// Shared with AddFormV1 (form/maxCeiling.js): a DOTTED reference is absolute
// (reaches out of the validated field's container), a bare one is a sibling.
function getSiblingFieldName(currentName, compareField) {
    return resolveCompareFieldName(currentName, compareField);
}

// Date comparison + picker-constraint helpers now live in ONE place
// (components/form/dateRules.js) and are imported at the top of this file.
// They used to be duplicated verbatim between AddFormV1 and EditFormV1, which
// is how a fix lands on Add and silently misses Edit.

const crossFieldNumberRule = ({ label, message, value, rule, form, name, compare, text }) => ({
    validator: async (_, input) => {
        const compareField = value ?? rule.compareField ?? rule.field;
        if (!compareField || empty(input)) return Promise.resolve();
        const compareValue = form.getFieldValue(getSiblingFieldName(name, compareField));
        if (empty(compareValue)) return Promise.resolve();
        const inputNumber = Number(input);
        const compareNumber = Number(compareValue);
        if (Number.isNaN(inputNumber) || Number.isNaN(compareNumber)) return Promise.resolve();
        return compare(inputNumber, compareNumber)
            ? Promise.resolve()
            : Promise.reject(new Error(message ?? `${label} ${text} ${compareField}`));
    },
});

const validationRulesByType = {
    // The pre-existing minLength3 / minLength5 pair baked the threshold into the
    // TYPE NAME, and existed only in AddFormV1 — configuring one on a field did
    // nothing at all on the Edit form. Both now delegate to the generic `min`
    // rule (admin: "Min length/value"), which already implements exactly this
    // for non-number fields and is present in BOTH forms.
    minLength3: (args) => validationRulesByType.min({ ...args, value: 3 }),
    minLength5: (args) => validationRulesByType.min({ ...args, value: 5 }),
    noPastDate: ({ label, message: msg }) => ({
        validator: (_, value) => {
            if (empty(value)) return Promise.resolve();
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            return getComparableDateTime(value) < today.getTime()
                ? Promise.reject(new Error(msg ?? `${label} cannot be a past date`))
                : Promise.resolve();
        },
    }),
    noFutureDate: ({ label, message: msg }) => ({
        validator: (_, value) => {
            if (empty(value)) return Promise.resolve();
            const endOfDay = new Date();
            endOfDay.setHours(23, 59, 59, 999);
            return getComparableDateTime(value) > endOfDay.getTime()
                ? Promise.reject(new Error(msg ?? `${label} cannot be a future date`))
                : Promise.resolve();
        },
    }),
    minAge: ({ message: msg, value }) => ({
        validator: (_, input) => {
            if (empty(input)) return Promise.resolve();
            const years = Number(value ?? 18);
            const cutoff = new Date();
            cutoff.setFullYear(cutoff.getFullYear() - years);
            cutoff.setHours(23, 59, 59, 999);
            return getComparableDateTime(input) > cutoff.getTime()
                ? Promise.reject(new Error(msg ?? `Must be at least ${years} years old`))
                : Promise.resolve();
        },
    }),
    // Both date-pair rules delegate to the shared engine, so `strict` (reject an
    // equal date — the 7-July bug) and `minGap`/`minGapFrom` (the duration
    // dropdown) behave identically on Add and Edit, and the SAME rule object
    // also drives the picker's disabledDate below.
    dateAfterField: ({ label, message: msg, value, rule, form, name }) => buildPairValidator({
        type: 'dateAfterField',
        label,
        rule: { ...rule, value: value ?? rule?.compareField ?? rule?.field, message: msg },
        readSibling: (key) => form.getFieldValue(getSiblingFieldName(name, key)),
    }),
    dateBeforeField: ({ label, message: msg, value, rule, form, name }) => buildPairValidator({
        type: 'dateBeforeField',
        label,
        rule: { ...rule, value: value ?? rule?.compareField ?? rule?.field, message: msg },
        readSibling: (key) => form.getFieldValue(getSiblingFieldName(name, key)),
    }),
    timeAfterField: ({ label, message: msg, value, rule, form, name }) => ({
        validator: async (_, input) => {
            const compareField = value ?? rule.compareField ?? rule.field;
            if (!compareField || empty(input)) return Promise.resolve();
            const compareValue = form.getFieldValue(getSiblingFieldName(name, compareField));
            if (empty(compareValue)) return Promise.resolve();
            const inputTime = getComparableTimeOfDay(input);
            const compareTime = getComparableTimeOfDay(compareValue);
            if (inputTime === null || compareTime === null || inputTime > compareTime) return Promise.resolve();
            return Promise.reject(new Error(msg ?? `${label} must be after ${compareField}`));
        },
    }),
    timeBeforeField: ({ label, message: msg, value, rule, form, name }) => ({
        validator: async (_, input) => {
            const compareField = value ?? rule.compareField ?? rule.field;
            if (!compareField || empty(input)) return Promise.resolve();
            const compareValue = form.getFieldValue(getSiblingFieldName(name, compareField));
            if (empty(compareValue)) return Promise.resolve();
            const inputTime = getComparableTimeOfDay(input);
            const compareTime = getComparableTimeOfDay(compareValue);
            if (inputTime === null || compareTime === null || inputTime < compareTime) return Promise.resolve();
            return Promise.reject(new Error(msg ?? `${label} must be before ${compareField}`));
        },
    }),
    greaterThanField: (args) => crossFieldNumberRule({ ...args, compare: (input, other) => input >= other, text: 'must be greater than or equal to' }),
    strictGreaterThanField: (args) => crossFieldNumberRule({ ...args, compare: (input, other) => input > other, text: 'must be greater than' }),
    strictLessThanField: (args) => crossFieldNumberRule({ ...args, compare: (input, other) => input < other, text: 'must be less than' }),
    lessThanOrEqualField: (args) => crossFieldNumberRule({ ...args, compare: (input, other) => input <= other, text: 'must be less than or equal to' }),
    // maxFromField — { type:'maxFromField', field:'<otherKey>', message }. Same
    // rule object AddFormV1 uses (form/crossFieldRules.js): the referenced field
    // is resolved through renderField's `prefixFor`, i.e. with the scoping a
    // Show Condition uses, so a rule inside an addRow row can cap itself against
    // a TOP-LEVEL field. Which field caps which is Form Groups config.
    maxFromField: (args) => maxFromFieldRule(args),
    // Identical to AddFormV1's — the shared decision core (incl. the "a 0
    // ceiling means no ceiling" rule) lives in form/maxCeiling.js.
    maxFieldWithFallback: ({ label, message: msg, value, rule, form, name }) => ({
        validator: async (_, input) => {
            const result = checkMaxWithFallback({
                input,
                compareFields: parseCompareFields(value ?? rule.compareFields),
                readValue: (fieldName) => readCompareValue(form, name, fieldName),
            });
            if (result.ok) return Promise.resolve();
            return Promise.reject(new Error(msg ?? `${label} must not exceed ${result.field}`));
        },
    }),
    required: ({ field, message: msg, form, name }) => ({
        validator: (_, value) => {
            // A field disabled via disabledByField (e.g. endDate while
            // currentlyPursuing is checked) must not block submit with a
            // "required" error the user has no way to satisfy.
            if (field?.disabledByField && form && name) {
                const disabledNow = truthy(form.getFieldValue(getSiblingFieldPath(name, field.disabledByField)));
                if (disabledNow) return Promise.resolve();
            }
            // requiredIfField — e.g. an alternate phone's country code becomes
            // mandatory only once the phone number itself has a value. Mirrors
            // AddFormV1 (they used to diverge: this half existed only there).
            if (field?.requiredIfField && form) {
                const triggerValue = form.getFieldValue(getSiblingFieldPath(name, field.requiredIfField));
                if (!truthy(triggerValue)) return Promise.resolve();
            }
            let isEmpty =
                value === undefined || value === null || value === '' ||
                (Array.isArray(value) && value.length === 0);
            if (!isEmpty && field?.type === 'text-editor') isEmpty = stripHtml(value) === '';
            return isEmpty
                ? Promise.reject(new Error(msg ?? 'Mandatory Field'))
                : Promise.resolve();
        },
    }),
    email: ({ label, message: msg }) => configuredEmailRule(label, msg),
    // Admin-configured "already present" check — same shared implementation
    // AddFormV1 uses; the only difference is the recordId the edit context adds.
    unique: ({ field, rule, uniqueContext }) => buildUniqueValidationRule({
        field, validation: rule, context: uniqueContext,
    }),
    url: ({ label, message: msg }) => ({ type: 'url', message: msg ?? `Enter valid ${label}` }),
    number: ({ label, message: msg }) => ({ pattern: /^[0-9]+$/, message: msg ?? `${label} must be a number` }),
    phone: ({ message: msg }) => ({
        validator: (_, value) => {
            if (value === undefined || value === null || value === '') return Promise.resolve();
            const { total } = parsePhoneFormat();
            return phoneDigits(value).length === total
                ? Promise.resolve()
                : Promise.reject(new Error(msg ?? `Phone number must contain exactly ${total} digits`));
        },
    }),
    len: ({ label, message: msg, value }) => ({ len: Number(value), message: msg ?? `${label} must be ${value} characters` }),
    pattern: ({ label, message: msg, rule }) => ({
        pattern: new RegExp(rule.pattern ?? rule.value, rule.flags),
        message: msg ?? `Enter valid ${label}`,
    }),
    min: ({ field, label, message: msg, value }) =>
        field.type === 'number'
            ? numberRangeRule({ label, message: msg, value, compare: (i, m) => i >= m, text: 'must be at least' })
            : {
                validator: (_, input) => {
                    if (input == null || input === '') return Promise.resolve();
                    const content = field.type === 'text-editor' ? stripHtml(input) : String(input);
                    if (content === '') return Promise.resolve();
                    return content.length >= Number(value)
                        ? Promise.resolve()
                        : Promise.reject(new Error(msg ?? `${label} must be at least ${value} characters`));
                },
            },
    max: ({ field, label, message: msg, value }) =>
        field.type === 'number'
            ? numberRangeRule({ label, message: msg, value, compare: (i, m) => i <= m, text: 'must not exceed' })
            : {
                validator: (_, input) => {
                    if (input == null || input === '') return Promise.resolve();
                    const content = field.type === 'text-editor' ? stripHtml(input) : String(input);
                    if (content === '') return Promise.resolve();
                    return content.length <= Number(value)
                        ? Promise.resolve()
                        : Promise.reject(new Error(msg ?? `${label} must not exceed ${value} characters`));
                },
            },
};

// `prefixFor` is renderField's showIf prefix resolver, forwarded so a rule that
// references another field resolves it with the same row-vs-top-level scoping
// the Show Conditions use (undefined at call sites with no row context).
function validationToRule(validation, field, form, name, prefixFor, uniqueContext) {
    const rule = typeof validation === 'string' ? { type: validation } : validation;
    const type = rule.type ?? rule.rule ?? rule.name;
    const value = rule.value ?? rule.length ?? rule.min ?? rule.max;
    const label = field.label ?? field.field ?? 'Field';
    const factory = validationRulesByType[type];
    return factory ? factory({ field, label, message: rule.message, rule, value, form, name, prefixFor, uniqueContext }) : rule;
}

function getSiblingFieldPath(name, siblingField) {
    return Array.isArray(name) && name.length > 1
        ? [...name.slice(0, -1), siblingField]
        : [siblingField];
}

// A field is required either via the simple "req" toggle or via a "required"
// entry in the admin-configured validations list — the Form.Item asterisk and
// the addRow "at least one row" check must honour both, not just field.req.
function isFieldRequired(field) {
    if (truthy(field?.req)) return true;
    const apiRules = field?.validations ?? field?.validation ?? field?.rules ?? [];
    return apiRules.some((rule) => (typeof rule === 'string' ? rule : rule?.type) === 'required');
}

// `uniqueContext` ({ checker, module, recordId, clientId, region }) turns a
// configured `unique` validation into a live preflight check. On EDIT it always
// carries the recordId so the record never collides with its own stored value.
// Call sites with no context leave the unique rule a no-op (the server-side
// check at submit stays authoritative either way).
function getRules(field, form, name, skipRequired = false, prefixFor = undefined, uniqueContext = undefined,
    extraRules = undefined) {
    const label = field.label ?? field.field ?? 'Field';
    const apiRules = field.validations ?? field.validation ?? field.rules ?? [];
    const hasApiRequired = apiRules.some((r) => (typeof r === 'string' ? r : r?.type) === 'required');
    const hasApiEmail = apiRules.some((r) => (typeof r === 'string' ? r : r?.type) === 'email');
    const rules = [];

    // Rules the CALLER resolved because they need the group, which this
    // function never sees — today, a region that refuses an in-progress entry.
    // They go first so a blocked row reports that before anything else.
    if (extraRules?.length) rules.push(...extraRules);

    // Cross-row hierarchy: a newest-first list constrains each row against its
    // NEIGHBOURS. The picker already greys the impossible dates out; this
    // catches one that arrived another way — typed, pasted, prefilled from a
    // résumé, or entered before the neighbouring row was filled in.
    if (field.rowHierarchy) {
        const ctx = rowContext(form, name);
        if (ctx.rows) rules.push(buildRowHierarchyValidator({ field, ...ctx }));
    }

    if (!skipRequired && !hasApiRequired && (truthy(field.req) || field.requiredIfField)) {
        rules.push(validationRulesByType.required({ field, message: 'Mandatory Field', form, name }));
    }
    if ((field.type === 'email' || field.formatter === 'email') && !hasApiEmail) {
        rules.push(configuredEmailRule(label));
    }
    // Number fields are whole-number-only unless the admin marks them decimal
    // (e.g. a commission of 5.5). Both patterns still reject any non-numeric
    // character, so "float only" stays enforced either way.
    if (field.type === 'number') {
        rules.push(truthy(field.decimal)
            ? { pattern: /^\d+(\.\d+)?$/, message: `${label} must be a number` }
            : { pattern: /^[0-9]+$/, message: `${label} must be a number` });
    }
    // attachUniqueSyncGuards re-wraps any `unique` rule so it only calls the
    // server once every synchronous rule in this same list has passed.
    // Admin override checkbox (field.overrideCheckbox) — suspends the validation
    // TYPES it names while ticked. Same mechanism as AddFormV1, so a field
    // configured once behaves identically on add and on edit.
    const buildRule = (r) => validationToRule(r, field, form, name, prefixFor, uniqueContext);
    const mapRule = overrideRuleMapper(field, name, prefixFor, buildRule);

    return attachUniqueSyncGuards([...rules, ...apiRules.map(mapRule), securityValidationRule(field)]);
}

function getFieldDependencies(field, currentName, prefixFor) {
    const supportedTypes = new Set([
        'greaterThanField', 'strictGreaterThanField', 'strictLessThanField',
        'lessThanOrEqualField', 'maxFieldWithFallback', 'dateAfterField', 'dateBeforeField',
        'timeAfterField', 'timeBeforeField',
    ]);
    // maxFromField is handled apart from the sibling-based list below because the
    // field it references may live outside this row — its dependency path has to
    // be built with the rule's own (showIf) scoping.
    const scoped = (field.validations ?? field.validation ?? field.rules ?? [])
        .filter((rule) => (typeof rule === 'string' ? rule : rule?.type) === 'maxFromField')
        .map((rule) => maxFromFieldDependency(rule, currentName, prefixFor))
        .filter(Boolean);
    return [...scoped, ...(field.validations ?? field.validation ?? field.rules ?? [])
        .filter((rule) => supportedTypes.has(typeof rule === 'string' ? rule : rule?.type))
        .flatMap((rule) => String(rule.value ?? rule.compareField ?? rule.field ?? '')
            .split(',').map((item) => item.trim()).filter(Boolean)
            .map((compareField) => getSiblingFieldName(currentName, compareField)))];
}

function getInputLimits(field) {
    const len = field.len ?? getValidationValue(field, 'len');
    const min = field.min ?? getValidationValue(field, 'min');
    const max = field.max ?? getValidationValue(field, 'max');
    if (field.type === 'number') return { min: min ?? undefined, max: max ?? undefined };
    return { minLength: min ?? undefined, maxLength: field.maxLength ?? len ?? max ?? undefined };
}

// Thin adapters over the shared engine (components/form/dateRules.js). The
// bodies used to live here AND in EditFormV1; the picker's greyed-out range and
// the submit-time error are now generated from the same rule object, so a
// calendar that disagrees with its own error message is structurally impossible.
// rowContext — the sibling ROWS of the repeatable group this field sits in,
// plus this field's row index.
//
// A per-row `readSibling` can only see the row it is in, but a newest-first
// list constrains a row against its NEIGHBOURS. Inside a Form.List the field
// path is [listName, rowIndex, fieldKey], which is exactly what is needed to
// read the whole list and locate this row within it.
//
// Returns {} outside a repeatable group, so the hierarchy simply never applies.
function rowContext(form, name) {
    if (!Array.isArray(name) || name.length < 3 || typeof name[1] !== 'number') return {};
    const rows = form.getFieldValue(name[0]);
    if (!Array.isArray(rows)) return {};
    return { rows, rowIndex: name[1] };
}

function getDisabledDate(field, form, name) {
    return buildDisabledDate({
        field,
        readSibling: (key) => form.getFieldValue(getSiblingFieldName(name, key)),
        ...rowContext(form, name),
    });
}

function getDefaultPickerValue(field) {
    return defaultPickerValue(field);
}

function cleanValue(value, formatter) {
    if (!formatter) return value;
    if (formatter === 'phone') return formatPhone(value);
    if (formatter === 'digits') return value.replace(/\D/g, '').slice(0, 10);
    if (formatter === 'email' || formatter === 'noSpaces') return value.replace(/\s/g, '');
    if (formatter === 'name') return value.replace(/\s+/g, ' ').trimStart().replace(/\b\w/g, (c) => c.toUpperCase());
    return value;
}

// ─── FieldControl ─────────────────────────────────────────────────────────────

function FieldControl({ field, form, name, value, checked, fileList, moduleName, recordId, initialValue, scopeValues = {}, onAiUpload, onRegionBlocked, onRowOrderChange, formDisabled, rowFieldKeys }) {
    const type = field.type ?? 'text';
    const baseDisabled = !editable(field, moduleName);

    // ALL hooks at the top — before any conditional returns
    const [dynamicOptions, setDynamicOptions] = useState(null);
    const [optionsLoading, setOptionsLoading] = useState(false);
    const [locationSearch, setLocationSearch] = useState('');
    const [lookupSearch, setLookupSearch] = useState('');
    const [masterSearch, setMasterSearch] = useState('');
    const [viewer, setViewer] = useState({ open: false, docs: [], index: 0 });
    const [inputError, setInputError] = useState(null);
    // Bumped after a quick create/edit ("Add More") to refetch dropdown options.
    const [refetchNonce, setRefetchNonce] = useState(0);
    const rawLookupRef = useRef([]);
    // Row-aware lookupFilterValueFrom — mirrors AddFormV1.jsx: same-row sibling
    // wins when non-empty, top-level path is the fallback for old configs.
    const insideListRow = Array.isArray(name) && name.length >= 3 && typeof name[1] === 'number';
    const topFilterPath = field.lookupFilterValueFrom ? namePathFromString(field.lookupFilterValueFrom) : [`__lookup_filter_${field.field}`];
    const rowFilterPath = (field.lookupFilterValueFrom && insideListRow)
        ? [...name.slice(0, 2), ...namePathFromString(field.lookupFilterValueFrom)]
        : ['__noop_row_filter__'];
    const topFilterValue = Form.useWatch(topFilterPath, form);
    const rowFilterValue = Form.useWatch(rowFilterPath, form);
    const formFilterValue = !empty(rowFilterValue) ? rowFilterValue : topFilterValue;
    // scopeValues (e.g. AddFormV1's client-gate id) only wins when it actually
    // carries a value — a key present but undefined/empty (e.g. EditFormV1's
    // `{ clientId }` when no clientId prop was passed, as on the Jobs edit
    // page) must fall back to the field's own live form value, or the lookup
    // filter is permanently starved and the dropdown never resolves a name.
    const scopeFilterValue = field.lookupFilterValueFrom
        ? scopeValues[field.lookupFilterValueFrom]
        : undefined;
    const lookupFilterValue = !empty(scopeFilterValue) ? scopeFilterValue : formFilterValue;
    const disabledBySiblingPath = field.disabledByField
        ? (Array.isArray(name) && name.length > 1 ? [...name.slice(0, -1), field.disabledByField] : [field.disabledByField])
        : ['__noop_disabled_watch__'];
    const disabledByValue = Form.useWatch(disabledBySiblingPath, form);
    // disabledIf — conditional read-only, same operator set as showIf (eq/neq/
    // truthy/falsy/in/notIn), e.g. lock the Employer to the configured default
    // when Contract Type is W2. The row value wins when the condition field
    // lives in this row; otherwise the top-level field drives it.
    const disabledIfTopPath = field.disabledIf?.field ? namePathFromString(field.disabledIf.field) : ['__noop_disabled_if__'];
    const disabledIfRowPath = (field.disabledIf?.field && insideListRow)
        ? [...name.slice(0, 2), ...namePathFromString(field.disabledIf.field)]
        : ['__noop_disabled_if_row__'];
    const disabledIfTop = Form.useWatch(disabledIfTopPath, form);
    const disabledIfRow = Form.useWatch(disabledIfRowPath, form);
    const conditionallyDisabled = field.disabledIf?.field
        ? labelConditionMatches(field.disabledIf, !empty(disabledIfRow) ? disabledIfRow : disabledIfTop)
        : false;
    // optionsFromField (admin config) — narrow this control's options to the live
    // value of ANOTHER field (see the identical block in AddFormV1). Resolved
    // with the scoping a Show Condition uses: the row's own field when the key
    // belongs to this row (rowFieldKeys), top level otherwise; row-first when
    // membership is unknown. Both paths are always watched — hooks cannot be
    // conditional — so the list re-narrows as soon as the source changes.
    const constraintKey = field.optionsFromField || '';
    const constraintTopPath = constraintKey ? namePathFromString(constraintKey) : ['__noop_options_from__'];
    const constraintRowPath = (constraintKey && insideListRow && (!rowFieldKeys || rowFieldKeys.has(constraintKey)))
        ? [...name.slice(0, 2), ...namePathFromString(constraintKey)]
        : ['__noop_options_from_row__'];
    const constraintTopValue = Form.useWatch(constraintTopPath, form);
    const constraintRowValue = Form.useWatch(constraintRowPath, form);
    const constraintValue = !empty(constraintRowValue) ? constraintRowValue : constraintTopValue;
    // prefillWhenExclusive (admin config) — a value a Conditional Default rule
    // would SET is EXCLUSIVE to that rule's condition: offered while the rule
    // matches, REMOVED from the list while it does not. Mirrors AddFormV1
    // exactly, so a field configured once behaves the same on add and on edit.
    //
    // HOOK SAFETY: the rule list is admin data and must never decide hook
    // count — ONE selector-form useWatch reads every referenced field out of
    // the whole store and reduces them to a string. Row-vs-top-level scoping is
    // the same one optionsFromField uses above.
    const exclusivePrefillRules = (truthy(field.prefillWhenExclusive) && field.prefillWhen?.length)
        ? field.prefillWhen
        : null;
    const exclusivePrefillKey = Form.useWatch(
        (values) => (exclusivePrefillRules
            ? JSON.stringify(prefillWhenWatchFields(exclusivePrefillRules).map((key) => {
                const read = (path) => path.reduce((acc, k) => (acc == null ? acc : acc[k]), values);
                const rowValue = (insideListRow && (!rowFieldKeys || rowFieldKeys.has(key)))
                    ? read([...name.slice(0, 2), ...namePathFromString(key)])
                    : undefined;
                return (!empty(rowValue) ? rowValue : read(namePathFromString(key))) ?? null;
            }))
            : ''),
        { form, preserve: true },
    );
    const exclusivePrefillWatched = useMemo(() => {
        if (!exclusivePrefillRules || !exclusivePrefillKey) return null;
        const values = JSON.parse(exclusivePrefillKey);
        const watched = {};
        prefillWhenWatchFields(exclusivePrefillRules).forEach((key, i) => { watched[key] = values[i]; });
        return watched;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [exclusivePrefillKey]);
    const groupDisabled = React.useContext(GroupDisabledContext);
    // exclusiveInGroup — only ONE row of a repeatable group may hold this flag.
    // See the matching comment in AddFormV1; watching the whole Form.List is
    // what makes this react to a change in a DIFFERENT row.
    const exclusiveListPath = (field.exclusiveInGroup && insideListRow)
        ? [name[0]]
        : ['__noop_exclusive_watch__'];
    const exclusiveRows = Form.useWatch(exclusiveListPath, form);
    const claimedByAnotherRow = useMemo(() => {
        if (!field.exclusiveInGroup || !insideListRow || !Array.isArray(exclusiveRows)) return false;
        const myIndex = name[1];
        const leaf = name[name.length - 1];
        return exclusiveRows.some((row, index) => index !== myIndex && truthy(row?.[leaf]));
    }, [field.exclusiveInGroup, insideListRow, exclusiveRows, name]);
    // locationMaster cascade (Country → State → City, sourced from the
    // locationMasters collection). See the identical block in AddFormV1.
    const locCountryTopPath = field.locationCountryFrom ? namePathFromString(field.locationCountryFrom) : ['__noop_loc_country__'];
    const locCountryRowPath = (field.locationCountryFrom && insideListRow)
        ? [...name.slice(0, 2), ...namePathFromString(field.locationCountryFrom)]
        : ['__noop_loc_country_row__'];
    const locCountryTop = Form.useWatch(locCountryTopPath, form);
    const locCountryRow = Form.useWatch(locCountryRowPath, form);
    const locCountryValue = !empty(locCountryRow) ? locCountryRow : locCountryTop;
    const locStateTopPath = field.locationStateFrom ? namePathFromString(field.locationStateFrom) : ['__noop_loc_state__'];
    const locStateRowPath = (field.locationStateFrom && insideListRow)
        ? [...name.slice(0, 2), ...namePathFromString(field.locationStateFrom)]
        : ['__noop_loc_state_row__'];
    const locStateTop = Form.useWatch(locStateTopPath, form);
    const locStateRow = Form.useWatch(locStateRowPath, form);
    const locStateValue = !empty(locStateRow) ? locStateRow : locStateTop;
    const locationLocked = field.dataSource === 'locationMaster' && (
        (field.locationLevel === 'state' && empty(locCountryValue))
        || (field.locationLevel === 'city' && (empty(locCountryValue) || empty(locStateValue)))
    );
    const disabled = baseDisabled || Boolean(field.disabled) || conditionallyDisabled || (field.disabledByField ? truthy(disabledByValue) : false) || Boolean(formDisabled) || groupDisabled || claimedByAnotherRow || locationLocked;

    useEffect(() => {
        if (type === 'location') {
            let cancelled = false;

            async function fetchLocations() {
                try {
                    setOptionsLoading(true);
                    const params = new URLSearchParams({
                        search: locationSearch,
                        limit: String(field.limit ?? 50),
                        offset: '0',
                    });
                    const json = await fetchJsonWithAuth(AUTH_URL, `/admin/location-dropdown-values?${params}`);
                    const raw = json?.data ?? json ?? [];
                    if (!cancelled) setDynamicOptions(normalizeOptions(Array.isArray(raw) ? raw : []));
                } catch (err) {
                    console.error(`[EditFormV1] Failed to fetch locations for "${field.field}":`, err);
                    if (!cancelled) setDynamicOptions([]);
                } finally {
                    if (!cancelled) setOptionsLoading(false);
                }
            }

            fetchLocations();
            return () => { cancelled = true; };
        }
        const ds = field.dataSource;
        if (!['select', 'radio', 'checkbox'].includes(type)) return undefined;
        if (!ds || ds === 'static') return undefined;
        if (ds === 'lookup' && field.lookupFilterField && empty(lookupFilterValue)) {
            setDynamicOptions([]);
            setOptionsLoading(false);
            return undefined;
        }

        let cancelled = false;

        async function fetchOptions() {
            try {
                setOptionsLoading(true);
                let raw = [];

                if (ds === 'master' || ds === 'module') {
                    raw = await getDropdownValues(
                        moduleName,
                        field.field,
                        ds === 'master' ? masterSearch : '',
                        field.limit ?? 50,
                        0,
                        {
                            dataSource: ds,
                            masterName: field.masterName,
                            groupName: field.groupName,
                        },
                    );
                } else if (ds === 'lookup') {
                    const params = new URLSearchParams({
                        collection: field.lookupCollection ?? '',
                        displayField: field.displayField ?? '',
                        valueField: field.valueField ?? '_id',
                    });
                    if (field.displayField2) params.set('displayField2', field.displayField2);
                    if (lookupSearch) params.set('search', lookupSearch);
                    if (field.lookupFilterField && !empty(lookupFilterValue)) {
                        params.set('filterField', field.lookupFilterField);
                        params.set('filterValue', String(lookupFilterValue));
                    }
                    // See AddFormV1 — rebuilt from the individual keys so this
                    // effect does not depend on the whole `field` object.
                    const rowFilterConfig = {
                        field: field.field,
                        extraFields: field.extraFields,
                        optionsRequireExtraField: field.optionsRequireExtraField,
                        optionsRequireExtraValue: field.optionsRequireExtraValue,
                        optionsHierarchyParentField: field.optionsHierarchyParentField,
                        optionsHierarchySelfFrom: field.optionsHierarchySelfFrom,
                    };
                    // The filter keys are added to extraFields automatically so
                    // config cannot be half-applied.
                    const extraKeys = lookupExtraFieldKeys(rowFilterConfig);
                    if (extraKeys.length) params.set('extraFields', extraKeys.join(','));
                    const json = await fetchJsonWithAuth(AUTH_URL, `/admin/lookup-dropdown-values?${params}`);
                    raw = json?.data ?? json ?? [];
                    raw = filterLookupOptionRows(raw, rowFilterConfig, { source: 'EditFormV1' });
                } else if (ds === 'locationMaster') {
                    const level = field.locationLevel || 'country';
                    if (level === 'state' && empty(locCountryValue)) {
                        raw = [];
                    } else if (level === 'city' && (empty(locCountryValue) || empty(locStateValue))) {
                        raw = [];
                    } else {
                        const params = new URLSearchParams({ level, limit: String(field.limit ?? 250), offset: '0' });
                        if (!empty(locCountryValue)) params.set('country', String(locCountryValue));
                        if (!empty(locStateValue)) params.set('state', String(locStateValue));
                        if (lookupSearch) params.set('search', lookupSearch);
                        const json = await fetchJsonWithAuth(AUTH_URL, `/admin/location-dropdown-values?${params}`);
                        raw = json?.data ?? json ?? [];
                    }
                } else if (ds === 'group') {
                    const groupName = String(field.groupName ?? '').toLowerCase().trim();
                    if (groupName === 'users' || groupName === 'user') {
                        const json = await fetchJsonWithAuth(AUTH_URL, '/get-all-users?limit=500&offset=0');
                        const users = json?.data?.users ?? json?.users ?? json?.data ?? [];
                        // groupRoleContains — see the identical block in AddFormV1.
                        const roleContains = String(field.groupRoleContains ?? '').trim().toLowerCase();
                        const filteredUsers = roleContains
                            ? (Array.isArray(users) ? users.filter((u) => String(u.roleName ?? '').toLowerCase().includes(roleContains)) : [])
                            : users;
                        raw = Array.isArray(filteredUsers)
                            ? filteredUsers.map((u) => ({
                                label: [u.firstName, u.lastName].filter(Boolean).join(' ').trim()
                                    || u.userName || u.email || String(u.userId ?? u.id),
                                value: u.userId ?? u.id,
                            }))
                            : [];
                    }
                }

                if (!cancelled) {
                    rawLookupRef.current = Array.isArray(raw) ? raw : [];
                    setDynamicOptions(normalizeOptions(rawLookupRef.current));
                }
            } catch (err) {
                console.error(`[EditFormV1] Failed to fetch options for "${field.field}":`, err);
                if (!cancelled) setDynamicOptions([]);
            } finally {
                if (!cancelled) setOptionsLoading(false);
            }
        }

        fetchOptions();
        return () => { cancelled = true; };
    }, [type, field.dataSource, field.field, field.lookupCollection,
        field.displayField, field.displayField2, field.valueField, field.lookupFilterField, lookupFilterValue, field.groupName, field.groupRoleContains, field.masterName, field.limit, locationSearch, lookupSearch, masterSearch, moduleName, field.extraFields, field.optionsRequireExtraField, field.optionsRequireExtraValue, field.optionsHierarchyParentField, field.optionsHierarchySelfFrom, refetchNonce, field.locationLevel, field.locationCountryFrom, field.locationStateFrom, locCountryValue, locStateValue]);

    // ── Derived values ──
    // The edit-form option fallback is a safety net for SELECT/RADIO fields
    // whose options didn't survive a legacy save. It must NOT touch a checkbox:
    // a boolean checkbox with options becomes a Checkbox.Group (Yes/No), so it
    // would render completely differently from the single checkbox the Add form
    // shows for the same field (e.g. "Willing to Relocate").
    const configuredOptions = [field.options, field.values]
        .find((items) => Array.isArray(items) && items.length > 0)
        ?? (type === 'checkbox' ? [] : getEditFormFallbackOptions(field));
    const staticOptions = normalizeOptions(configuredOptions);
    const baseOptions = dynamicOptions ?? staticOptions;

    // ── Stored values outside the filtered option list ──────────────────────
    // `lookupFilters` decide what may be PICKED. They must not decide what an
    // already-assigned value is allowed to SHOW — a job assigned to somebody
    // outside the current user's reporting chain rendered its raw id ("149")
    // instead of a name. Resolved separately, unfiltered, and appended.
    const [resolvedOptions, setResolvedOptions] = useState([]);
    const missingValues = missingOptionValues(value, baseOptions);
    // Joined into a string: the array identity changes every render, and a raw
    // array dependency would refetch forever.
    const missingKey = missingValues.join(',');
    useEffect(() => {
        if (!missingKey || field.dataSource !== 'lookup' || optionsLoading) return undefined;
        let cancelled = false;
        fetchLookupLabels(field, missingKey.split(','))
            .then((rows) => { if (!cancelled) setResolvedOptions(rows); })
            // Best effort. Failing to resolve a label must never break the
            // field — the value is still there and still submits.
            .catch(() => { if (!cancelled) setResolvedOptions([]); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [missingKey, field.dataSource, field.lookupCollection, field.displayField,
        field.displayField2, field.valueField, optionsLoading]);
    // optionsFromField constraint applied once, here, so EVERY option-driven
    // control (select, radio, checkbox group, location…) narrows the same way.
    // Fails open — see narrowOptionsByConstraint. prefillWhenExclusive then
    // COMPOSES on top of that narrowing (it removes, it never restores).
    const optionsWithStored = mergeResolvedOptions(baseOptions, resolvedOptions);
    const constrainedOptions = constraintKey ? narrowOptionsByConstraint(optionsWithStored, constraintValue) : optionsWithStored;
    const options = exclusivePrefillRules
        ? stripExclusivePrefillOptions(constrainedOptions, exclusivePrefillRules, exclusivePrefillWatched ?? {})
        : constrainedOptions;

    const selectMode = field.multiSelect
        ? 'multiple'
        : field.mode ?? (truthy(field.multiple) ? 'multiple' : undefined);
    const inferredSelectMode = selectMode ?? (effectiveDataType(field) === 'array' ? 'multiple' : undefined);
    // allowCustomValues (admin config) — keep a value the user TYPED even when it
    // is not in the list. Mirrors AddFormV1 exactly, so a field configured once
    // behaves the same on add and on edit. antd's "tags" mode always emits an
    // ARRAY, so a single-valued field is wrapped for display and unwrapped again
    // on change — the stored shape never changes.
    const allowCustomValues = truthy(field.allowCustomValues);
    const effectiveSelectMode = allowCustomValues ? 'tags' : inferredSelectMode;
    const isArrayValued = Boolean(field.multiSelect) || truthy(field.multiple)
        || effectiveDataType(field) === 'array' || inferredSelectMode === 'multiple';
    const tokenSeparators = allowCustomValues
        ? (typeof field.tokenSeparators === 'string' && field.tokenSeparators
            ? field.tokenSeparators.split('')
            : [','])
        : undefined;

    const isSelectLike = ['select', 'location'].includes(type);
    const placeholder = field.hidePlaceholder
        ? undefined
        : (field.placeholder ?? `${isSelectLike ? 'Select' : 'Enter'} ${String(field.label ?? '').toLowerCase()}`);
    const inputLimits = getInputLimits(field);

    const validateField = () => {
        setTimeout(() => { form.validateFields([name]).catch(() => { }); });
    };

    const updateAndValidate = (nextValue) => {
        form.setFieldValue(name, nextValue);
        validateField();
    };

    // Config-driven "Add More"/"Edit" quick actions inside a select dropdown.
    // Called unconditionally (rules of hooks); no-op unless the field opts in.
    const quick = useQuickField({
        field, form, name, moduleName, scopeValues, ownValue: value,
        onDone: (kind, newId) => {
            setRefetchNonce((n) => n + 1);
            const single = effectiveDataType(field) !== 'array' && !field.multiSelect && !field.multiple;
            if (kind === 'create' && newId != null && single) {
                // Same clearing the ordinary onChange does. Creating an employer
                // from inside the dropdown IS changing the employer, so the rest
                // of the group — recruiter, email, contact, VMS — still describes
                // the PREVIOUS one and must go. This path set the new value and
                // skipped the clear, so a brand-new employer kept the old
                // employer's recruiter beside it, which reads as a real pairing.
                //
                // Cleared BEFORE the value is written, so anything the new
                // selection autofills is preserved rather than wiped after.
                if (field.clearGroupOnChange || field.linkedClearField) {
                    applyGroupClear(form, field, name, rowFieldKeys);
                }
                updateAndValidate(usesLabeledSelectValue(field) ? { value: newId } : newId);
            }
        },
    });

    // Build validator config once (null when not set)
    const validatorCfg = field.validator?.type
        ? { ...field.validator, title: field.validator.title || field.label || 'Field' }
        : null;

    const inputProps = {
        ...inputLimits,
        value,
        disabled,
        placeholder,
        prefix: field.icon ? null : fieldIcons[field.field],
        onChange: (e) => {
            let next = field.formatter
                ? cleanValue(e.target.value, field.formatter)
                : e.target.value;

            if (validatorCfg) {
                const { cleaned, error } = applyInputValidator(next, validatorCfg, 'change');
                setInputError(error);
                next = cleaned;
            }

            updateAndValidate(next);
        },
        ...(validatorCfg && {
            onBlur: (e) => {
                const { cleaned, error } = applyInputValidator(e.target.value, validatorCfg, 'blur');
                setInputError(error);
                if (cleaned !== e.target.value) updateAndValidate(cleaned);
            },
            onKeyPress: (e) => {
                const pattern = getKeyPattern(validatorCfg.type);
                if (!pattern) return;
                if (e.which === 0 || e.keyCode === 8 || e.keyCode === 13) return;
                const char = String.fromCharCode(e.which ?? e.keyCode);
                if (!pattern.test(char)) {
                    e.preventDefault();
                    const hint = `${validatorCfg.title} — invalid character.`;
                    setInputError(hint);
                    setTimeout(() => setInputError((prev) => (prev === hint ? null : prev)), 1800);
                }
            },
            maxLength: validatorCfg.maxLength ?? inputLimits.maxLength,
        }),
    };

    // ── Renders ──

    if (type === 'tagify') {
        return (
            <Select value={value} allowClear disabled={disabled} mode="tags"
                placeholder={placeholder} onChange={updateAndValidate} />
        );
    }

    if (type === 'location') {
        return (
            <Select
                value={value}
                allowClear
                showSearch
                filterOption={false}
                disabled={disabled}
                loading={optionsLoading}
                // Single or multi is admin config (Form Groups → the field's
                // "Location selection"). inferredSelectMode is the SAME resolver
                // an ordinary select uses, so multiSelect / multiple / an array
                // dataType all mean the same thing on a location field as they
                // do everywhere else — and effectiveDataType already types a
                // multiSelect field's payload as an array, so nothing extra is
                // needed to store several locations.
                mode={inferredSelectMode}
                options={options}
                placeholder={placeholder}
                notFoundContent={optionsLoading ? <Spin size="small" /> : undefined}
                onSearch={setLocationSearch}
                onFocus={() => setLocationSearch((current) => current)}
                onChange={updateAndValidate}
            />
        );
    }

    if (type === 'select') {
        const remoteLookup = field.dataSource === 'lookup';
        const remoteMaster = field.dataSource === 'master';
        const remoteSearch = remoteLookup || remoteMaster;
        const keepLabel = usesLabeledSelectValue(field);
        const rawControlValue = toSelectControlValue(field, value, options);
        const controlValue = (allowCustomValues && !isArrayValued)
            ? (rawControlValue === undefined || rawControlValue === null || rawControlValue === ''
                ? undefined
                : (Array.isArray(rawControlValue) ? rawControlValue : [rawControlValue]))
            : rawControlValue;
        const handleSelectChange = (raw) => {
            // Unwrap "tags" mode's array back to the last entry for a
            // single-valued field, so free entry never changes what is stored.
            const val = (allowCustomValues && !isArrayValued && Array.isArray(raw))
                ? (raw.length ? raw[raw.length - 1] : undefined)
                : raw;
            updateAndValidate(val);
            // Same rule as the Add form (see components/form/clearGroupOnChange.js):
            // changing this field invalidates the rest of its group, so those
            // values are cleared before autofill writes the new selection's own.
            if (field.clearGroupOnChange || field.linkedClearField) {
                applyGroupClear(form, field, name, rowFieldKeys);
            }
            if (field.autofillFrom?.length) {
                const selectedValue = val && !Array.isArray(val) && typeof val === 'object' ? val.value : val;
                const isInsideList = Array.isArray(name) && name.length >= 3 && typeof name[1] === 'number';
                if (val == null) {
                    field.autofillFrom.forEach(({ targetField: tf }) => {
                        const tp = isInsideList
                            ? [...name.slice(0, 2), ...namePathFromString(tf)]
                            : namePathFromString(tf);
                        form.setFieldValue(tp, null);
                    });
                } else if (rawLookupRef.current.length > 0) {
                    const vField = field.valueField ?? '_id';
                    const rawItem = rawLookupRef.current.find(item =>
                        getNestedProp(item, vField) === selectedValue || item._id === selectedValue || item.id === selectedValue || item.value === selectedValue
                    );
                    if (rawItem) {
                        field.autofillFrom.forEach(({ optionKey, targetField: tf }) => {
                            const v = getNestedProp(rawItem, optionKey) ?? rawItem.extraData?.[optionKey] ?? null;
                            const tp = isInsideList
                                ? [...name.slice(0, 2), ...namePathFromString(tf)]
                                : namePathFromString(tf);
                            form.setFieldValue(tp, v);
                        });
                    }
                }
            }
        };
        return (
            <>
                <Select value={controlValue} allowClear disabled={disabled} loading={optionsLoading}
                    labelInValue={keepLabel || undefined}
                    // Every select is type-to-search — see the matching comment
                    // in AddFormV1. `field.showSearch: false` opts a field out.
                    showSearch={field.showSearch !== false}
                    optionFilterProp="label"
                    filterOption={remoteSearch ? false : undefined}
                    mode={effectiveSelectMode} tokenSeparators={tokenSeparators}
                    options={options} placeholder={placeholder}
                    notFoundContent={optionsLoading ? <Spin size="small" /> : undefined}
                    onSearch={remoteLookup ? setLookupSearch : remoteMaster ? setMasterSearch : undefined}
                    onChange={handleSelectChange}
                    dropdownRender={quick.footer ? (menu) => (<>{menu}{quick.footer}</>) : undefined} />
                {quick.modal}
            </>
        );
    }

    if (type === 'radio') {
        return (
            <Radio.Group value={value} disabled={disabled} options={options}
                onChange={(e) => updateAndValidate(e.target.value)} />
        );
    }

    if (type === 'checkbox') {
        if (options.length > 0) {
            return (
                <Checkbox.Group value={value} disabled={disabled} options={options}
                    onChange={updateAndValidate} />
            );
        }
        return (
            <Checkbox
                checked={checked}
                disabled={disabled}
                onChange={(e) => {
                    const next = e.target.checked;
                    // Region gate — see AddFormV1. The tick is REVERSED rather
                    // than left standing with an error underneath.
                    if (next && onRegionBlocked?.(field)) return;
                    updateAndValidate(next);
                    // Ticking "still ongoing" makes this the most recent entry,
                    // so it may now belong at the top of a newest-first list.
                    if (next) onRowOrderChange?.(field, name);
                    if (field.linkedClearField && next) {
                        const clearPath = Array.isArray(name) && name.length > 1
                            ? [...name.slice(0, -1), field.linkedClearField]
                            : [field.linkedClearField];
                        form.setFieldValue(clearPath, null);
                    }
                }}
            >
                {field.checkboxLabel ?? field.label}
            </Checkbox>
        );
    }

    if (type === 'text-editor') {
        return (
            <>
                <TipTapEditor
                    value={value ?? initialValue ?? ''}
                    disabled={disabled}
                    placeholder={placeholder}
                    onChange={(html) => {
                        if (validatorCfg) {
                            const { error } = applyInputValidator(html, validatorCfg, 'change');
                            setInputError(error);
                        }
                        updateAndValidate(html);
                    }}
                />
                {inputError && <div className="v1-input-validator-hint">{inputError}</div>}
            </>
        );
    }

    if (type === 'textarea') {
        return (
            <>
                <TextArea {...inputProps} rows={field.rows ?? 3} className="v1-scroll-textarea" />
                {inputError && <div className="v1-input-validator-hint">{inputError}</div>}
            </>
        );
    }

    if (type === 'date') {
        return (
            <DatePicker value={value} disabled={disabled} placeholder={placeholder}
                style={{ width: '100%' }} suffixIcon={<CalendarOutlined />}
                disabledDate={getDisabledDate(field, form, name)}
                defaultPickerValue={getDefaultPickerValue(field)}
                onChange={(date) => {
                    updateAndValidate(date);
                    // Dates are the other way a row becomes the most recent
                    // one — the tick-box is not the only trigger.
                    onRowOrderChange?.(field, name);
                }} />
        );
    }

    if (type === 'time') {
        return (
            <TimePicker value={value} disabled={disabled} placeholder={placeholder}
                style={{ width: '100%' }} onChange={(time) => updateAndValidate(time)} />
        );
    }

    if (type === 'file') {
        const fileRules = field.validations ?? field.validation ?? field.rules ?? [];
        const fileTypeRule = fileRules.find((r) => (typeof r === 'string' ? r : r?.type) === 'fileType');
        const fileSizeRule = fileRules.find((r) => (typeof r === 'string' ? r : r?.type) === 'fileSize');
        const maxFilesRule = fileRules.find((r) => (typeof r === 'string' ? r : r?.type) === 'maxFiles');

        // Admin "Allowed Content" category (field.accept) takes priority over the
        // fine-grained fileType rule; 'any'/unset falls through to the rule.
        const acceptCategory = uploadAcceptCategory(field);
        const acceptTypes = acceptCategory
            ? acceptCategory.exts.map((e) => `.${e}`).join(',')
            : (fileTypeRule?.value
                ? fileTypeRule.value.split(',').map((e) => `.${e.trim().replace(/^\./, '')}`).join(',')
                : undefined);
        const maxSizeMB = fileSizeRule ? Number(fileSizeRule?.value ?? 5) : undefined;
        // maxFiles validation rule takes top priority; otherwise use field.multiple/maxCount
        // (set via admin panel toggle), falling back to single-file mode.
        const maxFiles = maxFilesRule
            ? Number(maxFilesRule?.value ?? 1)
            : (truthy(field.multiple) ? (field.maxCount ?? 5) : 1);

        const fileList = Array.isArray(value) ? value : [];
        const canAddMore = fileList.length < maxFiles;

        const formatFileSize = (bytes) => {
            if (!bytes) return '';
            if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
            return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
        };

        const getFileIcon = (fileName) => {
            const ext = String(fileName ?? '').split('.').pop()?.toLowerCase();
            if (ext === 'pdf') return <FilePdfOutlined className="v1-file-type-icon v1-file-icon-pdf" />;
            if (['doc', 'docx'].includes(ext)) return <FileWordOutlined className="v1-file-type-icon v1-file-icon-word" />;
            if (['xls', 'xlsx', 'csv'].includes(ext)) return <FileExcelOutlined className="v1-file-type-icon v1-file-icon-excel" />;
            if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(ext)) return <FileImageOutlined className="v1-file-type-icon v1-file-icon-image" />;
            return <FileOutlined className="v1-file-type-icon v1-file-icon-default" />;
        };

        const handleFileDelete = (index) => {
            updateAndValidate(fileList.filter((_, i) => i !== index));
        };

        const handleFileView = async (file, fileIdx) => {
            // Newly selected (not-yet-saved) files are local browser Files and
            // can be previewed with a blob URL. Existing files are stored in a
            // private S3 bucket; rebuilding the raw bucket URL from
            // resumeLocation/file location returns 403. Resolve those files
            // through the common documents API first because it returns a
            // temporary presigned `fileUrl`.
            let registryDocuments = [];

            const hasExistingFiles = fileList.some((f) => !(f?.originFileObj instanceof File));
            if (hasExistingFiles && moduleName && recordId && field?.field) {
                try {
                    const response = await getModuleDocuments(moduleName, recordId, field.field);
                    registryDocuments = Array.isArray(response?.data) ? response.data : [];
                } catch (error) {
                    // Keep the legacy URL as a fallback. This still allows public
                    // files and locally-created blob URLs to preview normally.
                    console.warn('Unable to resolve presigned document URL:', error);
                }
            }

            const normalizedName = (value) => String(value ?? '')
                .split('?')[0]
                .split('#')[0]
                .split('/')
                .pop()
                ?.toLowerCase() ?? '';

            const docs = fileList.map((f, index) => {
                const localFile = f?.originFileObj instanceof File;
                if (localFile) {
                    return {
                        url: URL.createObjectURL(f.originFileObj),
                        name: f.name ?? f.originFileObj.name ?? 'file',
                        type: f.originFileObj.type || undefined,
                    };
                }

                const currentName = normalizedName(
                    f?.name ?? f?.fileName ?? f?.originalName ?? f?.url ?? f?.location,
                );

                // Prefer a filename match. For older document rows where the
                // filename metadata differs/missing, fall back to the same index
                // and finally to the first registry document for single-file
                // fields such as Resume.
                const signedDocument = registryDocuments.find((document) => {
                    const registryName = normalizedName(
                        document?.fileName
                        ?? document?.name
                        ?? document?.uploadedFileName
                        ?? document?.originalName
                        ?? document?.fileUrl
                        ?? document?.url,
                    );
                    return currentName && registryName && currentName === registryName;
                })
                    ?? registryDocuments[index]
                    ?? (fileList.length === 1 ? registryDocuments[0] : null);

                let url = signedDocument?.fileUrl
                    ?? signedDocument?.downloadUrl
                    ?? signedDocument?.signedUrl
                    ?? signedDocument?.url
                    ?? f?.fileUrl
                    ?? f?.downloadUrl
                    ?? f?.signedUrl
                    ?? f?.url
                    ?? f?.thumbUrl
                    ?? '';

                // Only rebuild a storage path when no presigned URL exists. For
                // private S3 this fallback may be forbidden, but keeping it here
                // preserves support for installations whose bucket is public.
                if (url && !url.startsWith('http') && !url.startsWith('blob:')) {
                    url = buildFileUrl(url);
                }

                return {
                    url,
                    name: signedDocument?.fileName
                        ?? signedDocument?.name
                        ?? f?.name
                        ?? 'file',
                    type: signedDocument?.mimeType
                        ?? signedDocument?.contentType
                        ?? f?.type
                        ?? undefined,
                };
            }).filter((document) => document.url);

            if (!docs.length) {
                message.error('Unable to resolve a preview URL for this document.');
                return;
            }

            // If some invalid entries were filtered out, point at the selected
            // document when possible without exceeding the resolved list.
            const nextIndex = Math.min(fileIdx ?? 0, docs.length - 1);
            setViewer({ open: true, docs, index: nextIndex });
        };

        const isMulti = maxFiles > 1;
        const remaining = maxFiles - fileList.length;

        const typeHint = acceptCategory
            ? acceptCategory.hint
            : (acceptTypes
                ? acceptTypes.split(',').map((t) => t.replace('.', '').toUpperCase()).join(', ')
                : null);
        const sizeHint = maxSizeMB ? `up to ${maxSizeMB} MB` : null;
        const baseHint = [typeHint, sizeHint].filter(Boolean).join(' ');
        const dropHint = isMulti && fileList.length > 0
            ? [baseHint, `${remaining} of ${maxFiles} remaining`].filter(Boolean).join(' • ')
            : baseHint;
        const dropTitle = fileList.length > 0
            ? `Add ${isMulti ? 'more files' : 'file'}`
            : (field.buttonLabel ?? `Upload ${field.label ?? 'File'}`);

        const beforeUploadHandler = (file) => {
            if (fileList.length >= maxFiles) return Upload.LIST_IGNORE;
            if (acceptTypes) {
                const ext = (file.name.split('.').pop() ?? '').toLowerCase();
                const allowed = acceptTypes.split(',').map((t) => t.replace(/^\./, '').toLowerCase());
                if (!allowed.includes(ext)) {
                    message.error(acceptCategory
                        ? acceptCategory.error
                        : (fileTypeRule?.message ?? `Only ${fileTypeRule.value} files are allowed`));
                    return Upload.LIST_IGNORE;
                }
            }
            if (maxSizeMB !== undefined && file.size / 1024 / 1024 > maxSizeMB) {
                message.error(fileSizeRule?.message ?? `File must be smaller than ${maxSizeMB} MB`);
                return Upload.LIST_IGNORE;
            }
            return false;
        };

        // antd calls onChange once per file even in multi-select, each time with the
        // full accumulated newList. Slicing to maxFiles guards against racing additions.
        const handleUploadChange = ({ fileList: newList }) => {
            const sliced = newList.slice(0, maxFiles);
            updateAndValidate(sliced);
            // An "upload"-trigger AI action (e.g. auto-parse a resume) fires once
            // per newly-added file, using the raw browser File object.
            if (onAiUpload && sliced.length > fileList.length) {
                const added = sliced[sliced.length - 1];
                if (added?.originFileObj) onAiUpload(added.originFileObj);
            }
        };

        const multiUploadProps = {
            accept: acceptTypes,
            fileList,
            showUploadList: false,
            multiple: true,
            maxCount: maxFiles,
            beforeUpload: beforeUploadHandler,
            onChange: handleUploadChange,
            disabled,
        };

        const fileRows = fileList.map((file, index) => (
            <div key={file.uid ?? index} className="v1-file-item">
                <div className="v1-file-item-icon">{getFileIcon(file.name)}</div>
                <div className="v1-file-item-info">
                    <span className="v1-file-item-name" title={file.name}>{file.name}</span>
                    <span className="v1-file-item-meta">
                        {file.existing
                            ? 'Previously uploaded'
                            : `${formatFileSize(file.size)}${file.size ? ' • ' : ''}Uploaded just now`}
                    </span>
                </div>
                <Space size={6} className="v1-file-item-actions">
                    <Button
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => handleFileView(file, index)}
                        className="v1-file-view-btn"
                    >
                        View
                    </Button>
                    <Button
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleFileDelete(index)}
                        className="v1-file-delete-btn"
                    />
                </Space>
            </div>
        ));

        const viewerEl = (
            <DocumentViewer
                documents={viewer.docs}
                open={viewer.open}
                onClose={() => setViewer(v => ({ ...v, open: false }))}
                initialIndex={viewer.index}
            />
        );

        if (isMulti) {
            const multiTitle = fileList.length > 0
                ? `Upload more ${field.label ?? 'files'}`
                : (field.buttonLabel ?? `Upload ${field.label ?? 'File'}`);

            return (
                <>
                    <div className="v1-file-upload-wrapper">
                        {/* Centered dropzone — always at top in multiple mode */}
                        {canAddMore && (
                            <Upload {...multiUploadProps} style={{ width: '100%', display: 'block' }}>
                                <div className={`v1-upload-dropzone v1-upload-dropzone-center${disabled ? ' v1-upload-dropzone-disabled' : ''}`}>
                                    <div className="v1-upload-dropzone-title-row">
                                        <CloudUploadOutlined className="v1-upload-cloud-icon v1-upload-cloud-icon-sm" />
                                        <span className="v1-upload-dropzone-title v1-upload-dropzone-title-blue">{multiTitle}</span>
                                    </div>
                                    <span className="v1-upload-dropzone-sub">Drag & drop or click to upload</span>
                                    {baseHint && <span className="v1-upload-dropzone-sub">{baseHint}</span>}
                                </div>
                            </Upload>
                        )}

                        {/* Uploaded file rows — 2-column grid */}
                        {fileList.length > 0 && (
                            <div className="v1-file-grid">
                                {fileRows}
                            </div>
                        )}

                        {/* "+ Add more files" text link at the bottom */}
                        {canAddMore && fileList.length > 0 && (
                            <Upload {...multiUploadProps} style={{ display: 'inline-block' }}>
                                <Button type="link" size="small" icon={<PlusOutlined />} className="v1-add-more-files-btn">
                                    Add more files
                                </Button>
                            </Upload>
                        )}
                    </div>
                    {viewerEl}
                </>
            );
        }

        return (
            <>
                <div className="v1-file-upload-wrapper">
                    {/* Single mode: compact file row at field height */}
                    {fileList.length > 0 && <div className="v1-file-rows-single">{fileRows}</div>}

                    {canAddMore && (
                        <Upload
                            accept={acceptTypes}
                            fileList={fileList}
                            showUploadList={false}
                            multiple={false}
                            maxCount={1}
                            beforeUpload={beforeUploadHandler}
                            onChange={handleUploadChange}
                            disabled={disabled}
                            style={{ width: '100%', display: 'block' }}
                        >
                            <div className={`v1-upload-dropzone v1-upload-dropzone-single${disabled ? ' v1-upload-dropzone-disabled' : ''}`}>
                                <CloudUploadOutlined className="v1-upload-cloud-icon" />
                                <span className="v1-upload-dropzone-title">{dropTitle}</span>
                                {dropHint && <span className="v1-upload-dropzone-sub">— {dropHint}</span>}
                            </div>
                        </Upload>
                    )}
                </div>
                {viewerEl}
            </>
        );
    }

    return (
        <>
            <Input
                {...inputProps}
                type={type === 'number' ? 'number' : type}
                // step="any" so the browser's own number input accepts a
                // fractional value on a field the admin marked decimal.
                {...(type === 'number' && truthy(field.decimal) ? { step: 'any' } : {})}
                // Admin-configured unit shown inside the input (e.g. "%").
                {...(field.suffix ? { suffix: field.suffix } : {})}
            />
            {inputError && <div className="v1-input-validator-hint">{inputError}</div>}
        </>
    );
}

// ─── SectionTitle ─────────────────────────────────────────────────────────────

function SectionTitle({ icon, title }) {
    return (
        <Space size={10} align="center">
            <div className="v1-section-icon-box">{icon}</div>
            <Text strong className="v1-section-label">{title}</Text>
        </Space>
    );
}

// ─── Main component ───────────────────────────────────────────────────────────


// renderFieldHelp — the guidance under a field.
//
// `helpText` accepts a STRING or an ARRAY of strings. An array renders as
// stacked lines, which is what lets one field carry two different KINDS of
// help without them running together: the mechanical constraint ("Supports
// .pdf, .doc, .docx up to 2MB") and the reason to bother ("Upload the résumé
// first — we can read the details straight out of it"). Jammed into one
// paragraph they read as a wall and the second half is skipped.
//
// A plain string keeps rendering exactly as before, so nothing existing moves.
function renderFieldHelp(field) {
    const raw = field?.helpText;
    const lines = (Array.isArray(raw) ? raw : [raw])
        .map((line) => (line == null ? '' : String(line).trim()))
        .filter(Boolean);
    if (!lines.length) return undefined;
    return (
        <span className="v1-field-help-text">
            {lines.map((line, i) => (
                <Text key={i} type="secondary" className="v1-field-help-line">{line}</Text>
            ))}
        </span>
    );
}

export default function EditFormV1({
    moduleName,
    groupName,
    recordId,
    authToken,
    clientId,
    region,
    breadcrumbItems = [],
    submitText = 'Update',
    cancelPath,
    onSuccess,
    // fieldNameMap: rename keys before sending to API
    // e.g. { mspReqId: 'mspreqId' }
    // Once backend standardises field names this prop can be removed
    fieldNameMap = {},
    // onFileUpload: (fieldName, file, recordId) => Promise
    // Handle multipart file uploads per module outside this component
    onFileUpload = null,
    // onBeforeSubmit: async (values) => boolean — called after validation but
    // BEFORE anything is saved. Return false (or resolve false) to abort the
    // save silently — e.g. a page that shows a preview/confirmation modal and
    // resumes the save only when the user confirms (Submission resubmit flow).
    onBeforeSubmit = null,
    // extraPayload — extra keys merged into the update payload on top of the
    // form values (mirrors AddFormV1's prop). Dotted keys become partial Mongo
    // updates — e.g. the resubmit page stamps the new internal status without
    // wiping the rest of the status object.
    extraPayload = null,
    // embedded / onCancel — render inside a quick "Add More"/"Edit" modal. When
    // embedded, onSuccess(recordId, values) is called after a successful save and
    // no navigation happens; the Cancel button calls onCancel. Page path unchanged.
    embedded = false,
    onCancel = null,
}) {
    const navigate = useNavigate();
    const location = useLocation();

    const [form] = Form.useForm();
    useEffect(() => {
        if (authToken) setAuthTokenOverride(authToken);
        return () => {
            if (authToken) clearAuthTokenOverride();
        };
    }, [authToken]);

    const [collapsed, setCollapsed] = useState({});
    const [formGroups, setFormGroups] = useState([]);
    // ── Central documents (the shared `documents` collection) ────────────────
    // A file is stored in TWO places, and for some modules only ONE of them.
    // The record may carry an inline container (passport.passportCopyLocation,
    // …), but an upload is ALSO registered in the shared documents collection —
    // and for jobs it is registered ONLY there: every job in the database holds
    // `jobAttachments: []` while its real files sit in `documents` keyed by
    // module + moduleRefId.
    //
    // The DETAIL view has always merged both (see DetailViewV1's centralDocs).
    // The edit form never read the registry at all, so a document that lives
    // only there was invisible here — the field rendered empty on a record that
    // demonstrably has files, which is the "job document does not show in the
    // edit form" report. Worse than cosmetic: saving from that state looks like
    // the user removed a file they were never shown.
    //
    // Fetched with the SAME call the detail view makes, so the two can never
    // disagree about what a record has.
    const [centralDocs, setCentralDocs] = useState([]);
    useEffect(() => {
        if (!moduleName || !recordId) { setCentralDocs([]); return undefined; }
        let cancelled = false;
        getModuleDocuments(moduleName, recordId)
            .then((docs) => {
                if (cancelled) return;
                setCentralDocs(Array.isArray(docs) ? docs : (docs?.data ?? []));
            })
            // Best effort: a registry that will not load must not stop the record
            // being edited — the inline values still render.
            .catch(() => { if (!cancelled) setCentralDocs([]); });
        return () => { cancelled = true; };
    }, [moduleName, recordId]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');
    // Role Configure → Form permissions for the CURRENT user's role, derived
    // server-side from this module's Form Groups config. null until loaded /
    // when unavailable, in which case normalizeGroups' rolePerms checks fail
    // open — no behavior change for a role/module that hasn't been configured.
    const [rolePerms, setRolePerms] = useState(null);
    const [defaultFieldSet, setDefaultFieldSet] = useState(() => new Set());

    // ── Client-scoped edit render ────────────────────────────────────────────
    // The counterpart of AddFormV1's job→client resolution: a record created
    // under a client's Form Groups variant must be EDITED with that same
    // variant. The record's own client is discovered from the first fetch
    // (getFormGroups rides `recordMeta` — { clientId, jobId } — along on the
    // groups array), then the config is refetched under that client scope.
    // Self-stabilising: the second pass resolves the same client and stops.
    const [resolvedClientId, setResolvedClientId] = useState('');
    const effectiveClientId = clientId || resolvedClientId;
    // The record's own linkage ids (recordMeta), used by the cross-module
    // carrier prefill below.
    const [recordMeta, setRecordMeta] = useState({});

    // Load common display defaults (phone/date format) once so phone fields
    // re-format to the admin-configured mask on edit too.
    useEffect(() => { loadDetailDefaults(); }, []);

    useEffect(() => {
        let cancelled = false;
        queueMicrotask(() => {
            if (cancelled) return;
            if (!moduleName) { setRolePerms(null); return; }
            // Same client/region scope the form's own structure is fetched
            // with (getFormGroups above) — a client-scoped Form Groups
            // override must gate the SAME scoped structure, not the
            // tenant-wide default.
            getRoleFormPermissions({ roleId: localStorage.getItem('roleId') }, moduleName, { clientId: effectiveClientId, region })
                .then((data) => { if (!cancelled) setRolePerms(Array.isArray(data) && data.length ? data : null); })
                .catch(() => { if (!cancelled) setRolePerms(null); });
        });
        return () => { cancelled = true; };
    }, [moduleName, effectiveClientId, region]);

    useEffect(() => {
        let cancelled = false;
        queueMicrotask(() => {
            if (cancelled) return;
            if (!moduleName) { setDefaultFieldSet(new Set()); return; }
            getModuleDefaultFields(moduleName)
                .then((keys) => { if (!cancelled) setDefaultFieldSet(new Set(Array.isArray(keys) ? keys : [])); })
                .catch(() => { if (!cancelled) setDefaultFieldSet(new Set()); });
        });
        return () => { cancelled = true; };
    }, [moduleName]);

    const groups = useMemo(() => normalizeGroups(formGroups, moduleName, rolePerms, defaultFieldSet), [formGroups, moduleName, rolePerms, defaultFieldSet]);


    // ── Newest-first row ordering ───────────────────────────────────────────
    // After the user changes a row's dates or ticks "still ongoing", check
    // whether that row now belongs somewhere else and ASK. Nothing is ever
    // reordered silently — a list that rearranges itself while you are typing
    // in it is disorienting, and the user may have a reason for the order.
    const handleRowOrder = useCallback(async (field, name) => {
        // Only meaningful inside a repeatable group: [listName, rowIndex, key].
        if (!Array.isArray(name) || name.length < 3 || typeof name[1] !== 'number') return;
        const groupName = name[0];
        const rowIndex = name[1];

        const group = groups.find((g) => g.name === groupName);
        if (!group) return;
        // Region-resolved, so a market that reorders differently is honoured.
        const effective = resolveRegionRules(group, getDefaults().region);
        if (!effective.orderBy) return;

        // Read AFTER the change has landed in the store, so the row being
        // judged carries the value the user just set rather than the previous
        // one — the whole decision turns on that value.
        await Promise.resolve();
        const rows = form.getFieldValue(groupName);
        if (!Array.isArray(rows)) return;

        await promptRowMove({
            group: effective,
            rows,
            rowIndex,
            move: listControlsRef.current[groupName]?.move,
            confirm: ({ title, body, okText, cancelText, from, to }) => openFormDecision({
                tone: 'suggestion',
                title,
                facts: [
                    { label: 'This entry is at', value: `Row ${from + 1}` },
                    { label: 'It belongs at', value: `Row ${to + 1}` },
                ],
                body,
                okText,
                cancelText,
            }),
        });
    }, [groups, form]);

    // ── Region gate ─────────────────────────────────────────────────────────
    // Identical rule to the Add form: some markets do not accept an entry that
    // is still in progress, and the region (Admin → Display Defaults) selects
    // which `regionRules` block the group applies. Reversing the tick beats
    // leaving it standing with an error underneath — the latter invites the
    // user to submit and be refused twice.
    const handleRegionBlocked = useCallback((field) => {
        const region = getDefaults().region;
        const group = groups.find((g) => (g.fields ?? []).some((f) => f.field === field.field));
        if (!group) return false;

        const effective = resolveRegionRules(group, region);
        const ongoingField = effective.orderBy?.inProgressField ?? 'currentStudyingHere';
        if (field.field !== ongoingField) return false;
        if (inProgressAllowed(effective)) return false;

        // Warn vs block. A warning explains the consequence and lets the tick
        // stand — recording a real person mid-degree is worth doing even where
        // they could not be put forward today. A block reverses the tick, and
        // the submit-time validator below refuses the row however it arrived.
        const blocking = inProgressBlocks(effective);
        openFormNotice({
            tone: blocking ? 'duplicate' : 'overwrite',
            title: blocking
                ? 'This has to be completed first'
                // States the CONSEQUENCE, not a vague heading. The user's next
                // action depends on knowing that saving this is fine but
                // submitting them later will not be.
                : 'You can add this, but they cannot be submitted',
            facts: [
                { label: 'Region', value: region || 'Common' },
                { label: 'Entry', value: group.label ?? group.name },
            ],
            body: inProgressError(effective),
            okText: blocking ? 'Got it' : 'Keep it anyway',
        });
        return blocking;
    }, [groups]);

    // ── The submit-time half of the same rule ───────────────────────────────
    // Reversing the tick-box only catches a row somebody ticked BY HAND. A
    // submission prefills its rows from the candidate, and the candidate is
    // allowed to hold an in-progress entry — so on a blocking region the row
    // arrives already ticked and nobody ever touched the box. Without a rule on
    // the field the form would happily submit it, and the restriction would
    // apply to exactly the users who did not need stopping.
    //
    // Resolved here rather than inside getRules because it depends on the
    // GROUP, which getRules never sees.
    const inProgressRulesFor = useCallback((field, groupName) => {
        const group = groups.find((g) => g.name === groupName);
        if (!group) return undefined;
        const effective = resolveRegionRules(group, getDefaults().region);
        const ongoingField = effective.orderBy?.inProgressField ?? 'currentStudyingHere';
        if (field.field !== ongoingField) return undefined;
        const rule = buildInProgressValidator(effective);
        return rule ? [rule] : undefined;
    }, [groups]);

    // ── Config-driven field uniqueness ──────────────────────────────────────
    // One checker per form session owns the request sequence, the
    // normalized-value cache and the pending count (see
    // form/uniqueFieldValidation.js). While a check is in flight the Update
    // button is disabled so a save can never race an unanswered check.
    const [uniquePending, setUniquePending] = useState(0);
    const uniqueChecker = useMemo(
        () => createUniqueChecker({ checkFieldUnique, onPendingChange: setUniquePending }),
        [],
    );
    // recordId is what makes the edit semantics work: the server excludes this
    // record, so re-saving an unchanged value (or one that differs only in case
    // or surrounding whitespace) is not a duplicate of itself.
    const uniqueContext = useMemo(() => ({
        checker: uniqueChecker,
        module: moduleName,
        recordId,
        clientId: effectiveClientId || undefined,
        region: region || undefined,
    }), [uniqueChecker, moduleName, recordId, effectiveClientId, region]);
    // Every unique-configured field in this form, used to map a submit-time
    // duplicate back onto its field when the response body was flattened.
    const uniqueRuleIndex = useMemo(() => collectUniqueRules(groups), [groups]);
    // group.linkGroup — addRow groups sharing the same value stay row-synced
    // (see form/linkedAddRowGroups.js). Empty for every group with no
    // linkGroup, i.e. every group that predates this feature.
    const linkedGroupSets = useMemo(() => computeLinkedSets(groups), [groups]);
    // Registry of each addRow group's live Form.List {add, remove}, written on
    // every render from inside the Form.List's own render-prop (synchronous
    // within the same commit, so it's fully populated before a user can click
    // anything). A linked group's "Add Row" / row-delete calls every member's
    // add()/remove() through this registry; unlinked groups just call their own.
    const listControlsRef = useRef({});
    const aiActions = useAiActions({ form, groups, module: moduleName });

    // Per-addRow-group Form.List initialValue (one row object per backend-embedded
    // stored row). Computed from the same groups the form renders, so it is ready
    // the moment each Form.List mounts. Linked groups are then padded to equal
    // length (see padLinkedInitialValues) — a no-op for every unlinked group.
    const addRowInitials = useMemo(() => {
        const out = {};
        groups.forEach((group) => {
            if (group.addRow) out[group.name] = buildAddRowInitial(group);
        });
        return padLinkedInitialValues(linkedGroupSets, out);
    }, [groups, linkedGroupSets]);

    const MODULE_LABELS = {
        jobs: { list: 'Jobs', path: '/jobs', edit: 'Edit Job' },
        candidates: { list: 'Candidates', path: '/candidates', edit: 'Edit Candidate' },
        submissions: { list: 'Submissions', path: '/submissions', edit: 'Edit Submission' },
    };

    const resolvedBreadcrumbs = embedded
        ? []
        : breadcrumbItems.length > 0
        ? breadcrumbItems
        : (() => {
            const config = MODULE_LABELS[normalizeModule(moduleName)];
            if (!config) return [];
            return [
                { label: 'Home', href: '/' },
                { label: config.list, href: config.path },
                { label: config.edit },
            ];
        })();


    // ── Fetch form config + record ──────────────────────────────────────────

    useEffect(() => {
        if (!moduleName || !recordId) return undefined;

        let cancelled = false;

        async function fetchData() {
            try {
                setLoading(true);
                setError('');
                setSubmitError('');

                // Single source of truth: the form-groups config with `id` embeds
                // each field's current value (field.value). No separate detail-view
                // fetch — prefill reads field.value only.
                const groupsResponse = await getFormGroups({ module: moduleName, id: recordId, clientId: effectiveClientId, region, group: groupName });

                if (cancelled) return;

                setFormGroups(Array.isArray(groupsResponse) ? groupsResponse : []);
                // recordMeta = every top-level "*Id" of the record (jobId,
                // applicantId, clientId, …). It is the edit-side counterpart of
                // AddFormV1's URL linkage, and it is what lets a cross-module
                // carrier field find its source record here (see below).
                setRecordMeta(groupsResponse?.recordMeta ?? {});

                // Resolve this record's client and, if it differs from the scope
                // we just fetched under, refetch so the edit form matches the
                // client variant the add form rendered. Only when the caller did
                // not pin a client explicitly.
                if (!clientId) {
                    const meta = groupsResponse?.recordMeta ?? {};
                    const idOf = (v) => (v && typeof v === 'object' ? (v.id ?? v.value ?? '') : (v ?? ''));
                    let recClient = String(idOf(meta.clientId) || '');
                    // Submissions denormalize clientId, but fall back to the job.
                    const jobId = String(idOf(meta.jobId) || '');
                    if (!recClient && jobId) {
                        try {
                            const jobGroups = await getFormGroups({ module: 'jobs', id: jobId });
                            recClient = String(idOf(jobGroups?.recordMeta?.clientId) || '');
                        } catch { /* keep the default scope */ }
                    }
                    if (!cancelled && recClient && recClient !== resolvedClientId) {
                        setResolvedClientId(recClient);
                    }
                }
            } catch (err) {
                console.error(`Failed to fetch ${moduleName} edit data:`, err);
                if (!cancelled) setError(err?.message || 'Failed to load record');
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        fetchData();
        return () => { cancelled = true; };
    }, [moduleName, groupName, recordId, clientId, effectiveClientId, resolvedClientId, region]);

    // ── Prefill form ────────────────────────────────────────────────────────
    //
    // Single source of truth: each field's `field.value`, embedded by getFormGroups
    // when an `id` is passed. Shaped and applied via the shared applyGroupValues
    // module, the same code an AI Action's response is applied through.

    useEffect(() => {
        if (loading || groups.length === 0) return;
        applyScalarFieldValues(form, groups);
    }, [loading, groups, form]);

    // Seed file fields from the central documents registry — see the centralDocs
    // fetch above for WHY (for jobs the registry is the only place the file
    // exists). Runs AFTER applyScalarFieldValues so the record's own inline
    // value always wins: a field that already shows its file must not be
    // rewritten from the registry, or a freshly re-uploaded document could be
    // replaced by an older registry row.
    //
    // Matching mirrors the detail view exactly: a document is filed under the
    // field's own key OR its fileKey (uploads register under the multipart part
    // name, so a `jobAttachments` field's document is stored as "file").
    useEffect(() => {
        if (loading || groups.length === 0 || centralDocs.length === 0) return;

        const byField = new Map();
        centralDocs.forEach((doc) => {
            const key = doc?.fieldName || doc?.field;
            if (!key) return;
            if (!byField.has(key)) byField.set(key, []);
            byField.get(key).push(doc);
        });

        const values = {};
        let wrote = false;
        groups.forEach((group) => {
            if (truthy(group.addRow)) return;
            (group.fields ?? []).forEach((field) => {
                if (!field?.field || !isUploadField(field)) return;
                const existing = form.getFieldValue(namePathFromString(field.field));
                if (Array.isArray(existing) ? existing.length > 0 : !empty(existing)) return;
                const docs = byField.get(field.field) ?? (field.fileKey ? byField.get(field.fileKey) : null);
                if (!docs?.length) return;
                // Newest first — the API already sorts that way; a single-file
                // field shows the current document, not its whole history.
                const capacity = truthy(field.multiple) ? Number(field.maxCount ?? 5) : 1;
                writeFormValue(values, field.field, docs.slice(0, capacity).map((doc) => ({
                    uid: String(doc._id ?? doc.s3Key ?? doc.fileName),
                    name: doc.fileName ?? doc.uniqueName ?? 'file',
                    status: 'done',
                    url: doc.fileUrl ?? doc.s3Key ?? '',
                    existing: true,
                    // The untouched registry row, so a save that does not
                    // re-upload can write the same reference straight back
                    // instead of guessing at the stored shape.
                    stored: doc,
                })));
                wrote = true;
            });
        });
        if (wrote) form.setFieldsValue(values);
    }, [loading, groups, centralDocs, form]);

    // ── Files carried in from another form ──────────────────────────────────
    //
    // Someone starts adding a candidate, uploads a résumé, and we tell them the
    // person is already on file and send them here. The file has to come WITH
    // them: they picked that résumé for that person, and adding it to the
    // existing record is the entire reason they accepted the redirect. Dropping
    // it would make them find and attach it again.
    //
    // Runs AFTER the record's own values are applied, so the carried file wins
    // over whatever is currently stored — that is the newer document, and file
    // versioning keeps the previous one rather than replacing it.
    const carriedFiles = location.state?.carryFiles;
    useEffect(() => {
        if (loading || !carriedFiles) return;
        const entries = Object.entries(carriedFiles)
            .filter(([, list]) => Array.isArray(list) && list.length > 0);
        if (!entries.length) return;
        form.setFieldsValue(Object.fromEntries(entries));
        message.info('The résumé you uploaded has been attached here — save to keep it.');
        // Consumed once: a refresh or a Back must not silently re-attach a file
        // the user has since removed.
        navigate(location.pathname + location.search, {
            replace: true,
            state: { ...(location.state ?? {}), carryFiles: null },
        });
    }, [loading, carriedFiles, form, navigate, location.pathname, location.search, location.state]);

    // ── Cross-module CARRIER prefill (field.prefillFromModule) ───────────────
    //
    // AddFormV1 fills prefillFromModule fields from the URL linkage (?jobId=).
    // On edit there is no such linkage — but the record itself stores its ids,
    // and getFormGroups surfaces them as recordMeta. So the SAME config pair
    // (prefillFromModule + prefillFrom) resolves here too.
    //
    // Scope: only NON-PERSISTENT carriers (payloadMode "skip", see
    // form/contextPrefill.js). A carrier is never written to the record, so a
    // validation that leans on it (e.g. "rate must not exceed the job's client
    // rate") would be silently INERT on edit unless it is re-derived here.
    // Stored fields are deliberately left alone: re-pulling the source module's
    // current value on edit would overwrite what the user saved.
    const carrierModules = useMemo(
        () => contextPrefillModules(
            groups.map((g) => ({ ...g, fields: (g.fields ?? []).filter(isNonPersistentCarrier) })),
            normalizeModule,
        ),
        [groups],
    );

    const [carrierRecords, setCarrierRecords] = useState({});
    useEffect(() => {
        if (carrierModules.length === 0) return undefined;
        let cancelled = false;
        carrierModules.forEach(async (mod) => {
            const id = contextRecordId(mod, recordMeta);
            if (!id) return;
            try {
                const data = await getFormGroups({ module: mod, id });
                const record = flattenContextRecord(extractContextGroups(data));
                if (!cancelled && Object.keys(record).length > 0) {
                    setCarrierRecords((prev) => ({ ...prev, [mod]: record }));
                }
            } catch (err) {
                // Fail open: a carrier that can't be resolved simply leaves its
                // rule without a ceiling, exactly like a form with no linkage.
                console.error(`Carrier prefill fetch failed for ${mod}:`, err);
            }
        });
        return () => { cancelled = true; };
    }, [carrierModules, recordMeta]);

    useEffect(() => {
        if (loading || groups.length === 0 || Object.keys(carrierRecords).length === 0) return;
        const values = {};
        let wrote = false;
        groups.forEach((group) => (group.fields ?? []).forEach((field) => {
            if (!field.field || !field.prefillFromModule || !isNonPersistentCarrier(field)) return;
            const source = carrierRecords[normalizeModule(field.prefillFromModule)];
            const stored = pickContextValue(source, field);
            if (stored === undefined || stored === null) return;
            // writeFormValue, not values[field.field]: a dotted key registers as
            // an ARRAY name path, so a flat dotted object key is a name nothing
            // listens on (the bug that made prefillFromModule silently do
            // nothing for nested fields on the add form).
            writeFormValue(values, field.field, stored);
            wrote = true;
        }));
        if (wrote) form.setFieldsValue(values);
    }, [carrierRecords, loading, groups, form]);

    // ── Collapse toggle ─────────────────────────────────────────────────────

    const toggle = (key) => {
        setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));
    };


    // ── Collapsible sections (group.allowCollapse / group.defaultCollapsed) ──
    // Collapsing is OPT-IN. A group that does not ask for it renders with no
    // chevron and can never be collapsed — which is the behaviour every form
    // had before this existed, so nothing already configured changes.
    //
    // `defaultCollapsed` only means anything for a group that allows collapsing
    // at all: a section that starts closed but cannot be opened would be a
    // section the user simply cannot fill in.
    //
    // Seeded once per group set rather than on every render, so a user who
    // opens a default-closed section does not have it slam shut again on the
    // next state change. `collapsedSeeded` tracks WHICH group set has been
    // applied, so a module switch (or a config reload) re-seeds exactly once.
    const collapsedSeedKey = useMemo(
        () => groups.map((g) => `${g.name}:${truthy(g.allowCollapse) ? 1 : 0}${truthy(g.defaultCollapsed) ? 1 : 0}`).join('|'),
        [groups],
    );
    const collapsedSeeded = useRef(null);
    useEffect(() => {
        if (!groups.length || collapsedSeeded.current === collapsedSeedKey) return;
        collapsedSeeded.current = collapsedSeedKey;
        const seeded = {};
        groups.forEach((group, index) => {
            const key = group.name ?? group.id ?? String(index);
            if (truthy(group.allowCollapse) && truthy(group.defaultCollapsed)) seeded[key] = true;
        });
        setCollapsed(seeded);
    }, [collapsedSeedKey, groups]);

    // ── Payload builder — the SAME config-driven engine AddFormV1 uses ──────────
    //
    // No field-name / module-specific logic. The engine:
    //   • starts from the untouched record (base) so unedited fields are preserved
    //   • applies each field's payloadKey / dataType / payloadMode / template
    //   • skips file fields (uploaded separately via onFileUpload)
    //
    // fieldNameMap is an optional caller-provided rename map kept for backward
    // compatibility; new renames should use the field's payloadKey config instead.
    function buildPayload(values) {
        // No base record: every editable value comes from the form (prefilled from
        // getFormGroups field.value). The payload carries only the configured fields.
        const payload = buildConfigPayload(values, groups, { module: moduleName });

        Object.entries(fieldNameMap).forEach(([from, to]) => {
            if (payload[from] !== undefined) {
                payload[to] = payload[from];
                delete payload[from];
            }
        });

        return payload;
    }

    // ── Submit ──────────────────────────────────────────────────────────────

    async function submit(values) {
        // Pre-submit gate (see onBeforeSubmit prop). Runs before the spinner so
        // an aborted save leaves the form fully interactive.
        if (onBeforeSubmit) {
            const proceed = await Promise.resolve()
                .then(() => onBeforeSubmit(values))
                .catch(() => false);
            if (proceed === false) return;
        }
        try {
            setSubmitting(true);
            setSubmitError('');

            // New files ride along in the SAME module/update multipart, exactly
            // like create — config-driven fileKey (jobs → "file", candidates →
            // resume/passport/documents/…). The downstream update handler stores
            // them and returns name/location. This replaces the old per-module
            // onFileUpload endpoints (which pointed candidate edits at the jobs
            // attachment URL). onFileUpload is still honoured as an optional
            // override when a module needs bespoke upload handling.
            // Indexed keys (documents[0], documents[1], …) so the update handler
            // can align each new file to its metadata row by position. Create uses
            // flat keys; only update needs indexing.
            const fileParts = collectFileParts(values, groups, { indexed: true, module: moduleName });

            if (onFileUpload && onFileUpload.useLegacyUpload) {
                const fileFieldDefs = groups
                    .flatMap((g) => g.fields ?? [])
                    .filter(isUploadField);
                for (const field of fileFieldDefs) {
                    const list = values[field.field];
                    if (!Array.isArray(list)) continue;
                    const newFiles = list.filter((f) => f.originFileObj instanceof File);
                    try {
                        await Promise.all(
                            newFiles.map((f) => onFileUpload(field.field, f.originFileObj, recordId)),
                        );
                    } catch (uploadErr) {
                        console.warn('File upload failed — continuing with save:', uploadErr);
                    }
                }
            }

            // Save JSON payload + new files together through the gateway.
            await updateRecord(moduleName, recordId, { ...buildPayload(values), ...(extraPayload ?? {}) },
                onFileUpload?.useLegacyUpload ? [] : fileParts);

            // Mirror AddFormV1's common document registry write so fresh uploads
            // added during edit are also discoverable via the shared documents
            // collection. Best-effort only: the record update already succeeded.
            const allFileParts = collectFileParts(values, groups, { module: moduleName });
            if (!onFileUpload?.useLegacyUpload && allFileParts.length > 0) {
                try {
                    await uploadModuleDocuments(moduleName, recordId, allFileParts);
                } catch (docErr) {
                    console.error('Document registry upload failed during edit:', docErr);
                    message.warning('Changes saved, but saving document records failed.');
                }
            }

            // Admin-configurable success toast (group.submitMessages); defaults to
            // "<Module> has Updated successfully" for every module, new ones included.
            const successText = submitSuccessMessage(groups, moduleName, 'edit');
            if (successText) message.success(successText);

            if (onSuccess) {
                onSuccess(recordId, values);
            } else {
                const afterSubmit = resolveAfterSubmit(groups);
                if (!afterSubmit) {
                    // No admin-configured behavior: go back to wherever the user
                    // opened this form FROM (usually the detail view), falling
                    // back to cancelPath only on a cold deep-link.
                    goBackOrTo(navigate, { cancelPath });
                } else if (afterSubmit.mode === 'back') {
                    goBackOrTo(navigate, { cancelPath });
                } else if (afterSubmit.mode === 'route' || afterSubmit.mode === 'detail') {
                    const target = safeNavTarget(afterSubmit.target, recordId, values);
                    if (target) navigate(target);
                    else goBackOrTo(navigate, { cancelPath });
                }
                // afterSubmit.mode === 'stay': no navigation.
            }
        } catch (err) {
            console.error(`Failed to update ${moduleName}:`, err);
            // Duplicate value rejected by the server: mark the field, keep the
            // form and everything typed into it, do not navigate, no success
            // toast, no page-level error banner.
            if (applyDuplicateFieldErrors(err)) return;
            const errorText = submitErrorMessage(groups, moduleName, 'edit', err?.message);
            setSubmitError(errorText);
            message.error(errorText);
        } finally {
            setSubmitting(false);
        }
    }

    // Submit-time duplicate ("already present") coming back from the
    // authoritative server-side check. The record was NOT saved, so the form
    // behaves exactly like a failed client-side validation: values kept, form
    // open, collapsed sections expanded, error under the offending field and
    // that field scrolled to + focused (shared utils/scrollToFirstFormError).
    //
    // The name used is the one the SERVER returned — the frontend field key,
    // which is not necessarily the stored field it maps to.
    function applyDuplicateFieldErrors(source) {
        const duplicates = extractDuplicateFieldErrors(source, { uniqueRules: uniqueRuleIndex });
        if (duplicates.length === 0) return false;

        // item.name is the FULL antd path: a plain key for an ordinary field, and
        // [group, rowIndex, key] for a field inside a repeatable (addRow) group,
        // so the error lands on the row the user actually typed into.
        form.setFields(duplicates.map((item) => ({ name: item.name ?? item.field, errors: [item.message] })));
        setCollapsed({});
        scrollToFirstFormError({
            errorFields: duplicates.map((item) => {
                const name = item.name ?? item.field;
                return { name: Array.isArray(name) ? name : [name], errors: [item.message] };
            }),
        }, form);
        return true;
    }

    // ── Scroll to first error on validation fail ────────────────────────────
    // Shared with AddFormV1 (utils/scrollToFirstFormError). Collapsed sections
    // are expanded FIRST — an error inside a display:none section can be
    // neither scrolled to nor focused, which made error navigation appear
    // broken on the edit form.
    const handleSubmitFailed = (info) => {
        setCollapsed({});
        scrollToFirstFormError(info, form);
    };

    // ── Render ──────────────────────────────────────────────────────────────

    const [combineToggles, setCombineToggles] = useState({});
    const fieldKey = (field) => String(field?.field ?? '');

    // See AddFormV1's identical helpers: a Show Condition may be a single leaf
    // ({field,operator,value}) or a `conditions` array combined by `logic`
    // ("and" default | "or") — the multi-condition "add more" form. `prefixFor`
    // resolves each referenced field's sibling prefix (a function), or a
    // precomputed prefix for legacy callers.
    const resolvePrefix = (prefixFor, key) => (typeof prefixFor === 'function' ? prefixFor(key) : prefixFor);
    const evalShowIfLeaf = (cond, getFieldValue, prefixFor) => {
        if (!cond?.field) return true;
        const prefix = resolvePrefix(prefixFor, cond.field);
        const path = prefix != null
            ? [...(Array.isArray(prefix) ? prefix : [prefix]), cond.field]
            : [cond.field];
        const val = getFieldValue(path);
        switch (cond.operator) {
            case 'eq': return String(val ?? '') === String(cond.value ?? '');
            case 'neq': return String(val ?? '') !== String(cond.value ?? '');
            case 'truthy': return val !== undefined && val !== null && val !== '' && val !== false;
            case 'falsy': return val === undefined || val === null || val === '' || val === false;
            // notEmpty targets an addRow GROUP's own Form.List name — true once
            // that group has at least one row. See AddFormV1.jsx's identical case.
            case 'notEmpty': return Array.isArray(val) ? val.length > 0 : Boolean(val);
            case 'in': return String(cond.value ?? '').split(',').map((v) => v.trim()).includes(String(val ?? ''));
            case 'notIn': return !String(cond.value ?? '').split(',').map((v) => v.trim()).includes(String(val ?? ''));
            default: return true;
        }
    };
    const evaluateShowIf = (showIf, getFieldValue, prefixFor) => {
        if (!showIf) return true;
        const conditions = Array.isArray(showIf.conditions) ? showIf.conditions.filter((c) => c?.field) : [];
        if (conditions.length) {
            const results = conditions.map((c) => evalShowIfLeaf(c, getFieldValue, prefixFor));
            const orLogic = String(showIf.logic ?? 'and').toLowerCase() === 'or';
            let combined = orLogic ? results.some(Boolean) : results.every(Boolean);
            if (showIf.field) {
                const leaf = evalShowIfLeaf(showIf, getFieldValue, prefixFor);
                combined = orLogic ? (combined || leaf) : (combined && leaf);
            }
            return combined;
        }
        return evalShowIfLeaf(showIf, getFieldValue, prefixFor);
    };
    const hasShowIf = (showIf) => Boolean(showIf?.field || showIf?.conditions?.some?.((c) => c?.field));

    const renderField = (field, namePrefix, listName, opts = {}) => {
        const fieldParts = typeof field.field === 'string' && field.field.includes('.')
            ? field.field.split('.')
            : null;
        const parsedField = fieldParts ?? field.field;
        const name = namePrefix !== undefined
            ? [namePrefix, ...(fieldParts ?? [field.field])]
            : parsedField;
        // listName is normally a single Form.List name (string), but a DOUBLY-nested
        // list (a repeat field's subFields rendered inside an addRow group row) passes
        // the full absolute path array of its own list — spread it instead of wrapping.
        const absoluteName = (listName !== undefined && namePrefix !== undefined)
            ? [...(Array.isArray(listName) ? listName : [listName]), ...(Array.isArray(name) ? name : [name])]
            : name;
        const isCheckboxGroup =
            field.type === 'checkbox' &&
            ((field.options && field.options.length > 0) ||
                (field.dataSource && field.dataSource !== 'static'));

        const valuePropName = !isCheckboxGroup && field.type === 'checkbox'
            ? 'checked'
            : 'value';

        // Inline (combined) fields show no per-box label — the combined card prints a
        // single label and the boxes sit side by side (e.g. From / To / Currency).
        // hideLabel: the group renders one shared header row above all addRow rows
        // instead of repeating each field's label on every row (e.g. Languages Known).
        const permissionLocked = Boolean(field.editablePermission)
            && !roleAllowsAction(field.editablePermission, moduleName);
        const showIfPrefix = (listName !== undefined && namePrefix !== undefined)
            ? [...(Array.isArray(listName) ? listName : [listName]), namePrefix]
            : null;
        // A condition may reference a sibling IN this row, or any top-level
        // field (e.g. contractType). Row prefix only applies when the referenced
        // key is one of this group's own fields; unknown membership (no
        // rowFieldKeys passed) keeps the historical row-first behavior.
        const prefixFor = (conditionKey) => (
            showIfPrefix && (!opts.rowFieldKeys || opts.rowFieldKeys.has(conditionKey))
                ? showIfPrefix
                : null
        );
        // Override checkbox rides the LABEL — see form/validationOverride.js.
        const overrideCfg = overrideConfig(field);
        const overrideFlag = overrideCfg ? overrideFlagPath(field, absoluteName, prefixFor) : null;
        const baseFieldLabel = (opts.inline || opts.hideLabel)
            ? ''
            : !isCheckboxGroup && field.type === 'checkbox'
            ? ''
            : <ConfiguredFieldLabel form={form} field={field} siblingPrefix={field.labelWhen?.field ? prefixFor(field.labelWhen.field) : showIfPrefix} locked={permissionLocked} />;
        const fieldLabel = (overrideCfg && baseFieldLabel) ? (
            <span className="v1-label-with-override">
                {baseFieldLabel}
                <ValidationOverrideCheckbox
                    form={form}
                    field={field}
                    flagPath={overrideFlag}
                    fieldPath={absoluteName}
                    disabled={permissionLocked}
                />
            </span>
        ) : baseFieldLabel;

        const mdSpan = opts.inline
            ? 24
            : field.type === 'text-editor' || (field.type === 'file' && truthy(field.multiple))
            ? 24
            : (field.col ?? 12);
        const dependencies = getFieldDependencies(field, absoluteName, prefixFor);
        // Admin-set help text (field.tooltip / infoEnabled+infoText). Rides the
        // label when there is one; a label-less field gets it on the whole item
        // (see tooltippedItem below) so every control type can carry one.
        const tooltipText = fieldTooltipText(field);

        // Inside a combined block, each box's width follows its combineWidth weight
        // (flex-grow), so the pack can be sized by ratio (e.g. 2:1, 1:2:3). Unset → 1.
        const inlineStyle = opts.inline
            ? { flex: `${Number(field.combineWidth) > 0 ? Number(field.combineWidth) : 1} 1 0`, maxWidth: 'none' }
            : undefined;

        // AI Actions: admin-configured buttons (Generate/Parse-style) attached to
        // this field. "click" actions render as buttons beside the field; an
        // "upload" action (on a file field) fires automatically when a new file
        // is accepted, via the onAiUpload callback passed into FieldControl.
        const clickAiActions = (field.aiActions || []).filter((a) => a.trigger === 'click');
        const uploadAiAction = (field.aiActions || []).find((a) => a.trigger === 'upload');

        const fieldItem = (
            <Form.Item
                label={fieldLabel}
                name={name}
                extra={renderFieldHelp(field)}
                required={isFieldRequired(field)}
                rules={getRules(field, form, absoluteName, false, prefixFor, uniqueContext,
                    inProgressRulesFor(field, opts.groupNameForActions))}
                dependencies={dependencies}
                valuePropName={valuePropName}
                getValueFromEvent={field.type === 'file' ? (e) => Array.isArray(e) ? e : e?.fileList : undefined}
                validateTrigger={['onBlur', 'onChange']}
            >
                <FieldControl
                    field={field}
                    form={form}
                    name={absoluteName}
                    rowFieldKeys={opts.rowFieldKeys}
                    moduleName={moduleName}
                    recordId={recordId}
                    initialValue={field.value}
                    scopeValues={{ clientId }}
                    onAiUpload={uploadAiAction
                        ? (file) => aiActions.runAction(opts.groupNameForActions, field, uploadAiAction, file)
                        : undefined}
                    formDisabled={aiActions.busy}
                    onRegionBlocked={handleRegionBlocked}
                    onRowOrderChange={handleRowOrder}
                />
            </Form.Item>
        );

        // A label-less field (inline/combined box, or a group printing one shared
        // header row) has nowhere to hang the label's info icon, so the whole
        // item becomes the tooltip target. The wrapping <span> is what Tooltip
        // attaches to — handing it the Form.Item directly would make antd clone
        // that element for the ref, and Form.Item's clone is reserved for
        // injecting value/onChange into its control.
        const tooltippedItem = (tooltipText && !fieldLabel)
            ? <Tooltip title={tooltipText}><span className="v1-field-tooltip-wrap">{fieldItem}</span></Tooltip>
            : fieldItem;

        const fieldItemWithActions = clickAiActions.length > 0 ? (() => {
            const positioned = groupActionsByPosition(clickAiActions);
            const group = (position) => (
                <AiActionButtonGroup
                    key={position}
                    position={position}
                    actions={positioned[position]}
                    aiActions={aiActions}
                    groupName={opts.groupNameForActions}
                    field={field}
                />
            );
            return (
                <div className="v1-ai-action-wrap">
                    {group('top-left')}
                    {group('top-center')}
                    {group('top-right')}
                    {tooltippedItem}
                    {group('bottom-left')}
                    {group('bottom-center')}
                    {group('bottom-right')}
                </div>
            );
        })() : tooltippedItem;

        // Hidden Form.Items for the verify carrier fields (verifyResultField
        // "isValid" / verifyTimestampField "validatedAt"), registered under the
        // same relative (row-indexed) name. On EDIT this is what makes an
        // already-verified email prefill as "Verified": the stored isValid=true
        // (embedded in the addRow row) populates this registered field, and
        // VerifyFieldButton renders the success label instead of the button.
        // It also keeps a re-verify persisting on update. Mirrors AddFormV1.
        const carrierName = (key) => (namePrefix !== undefined ? [namePrefix, key] : key);
        const verifyCarriers = field.verifyAction ? (
            <>
                {field.verifyResultField && (
                    <Form.Item name={carrierName(field.verifyResultField)} hidden noStyle>
                        <Input />
                    </Form.Item>
                )}
                {field.verifyTimestampField && (
                    <Form.Item name={carrierName(field.verifyTimestampField)} hidden noStyle>
                        <Input />
                    </Form.Item>
                )}
            </>
        ) : null;

        const fieldItemWithVerify = field.verifyAction ? (
            <div className="v1-verify-field-row">
                {fieldItemWithActions}
                <VerifyFieldButton form={form} field={field} absoluteName={absoluteName} />
                {verifyCarriers}
            </div>
        ) : fieldItemWithActions;

        // Hidden registration so the flag survives submit and prefills on edit —
        // same reason the verify carriers above are registered.
        const fieldItemWithOverride = overrideCfg ? (
            <>
                {fieldItemWithVerify}
                <Form.Item
                    name={namePrefix !== undefined
                        ? [namePrefix, overrideFlag[overrideFlag.length - 1]]
                        : overrideFlag[overrideFlag.length - 1]}
                    valuePropName="checked"
                    hidden
                    noStyle
                >
                    <Checkbox />
                </Form.Item>
            </>
        ) : fieldItemWithVerify;

        const prefillWatcher = field.prefillWhen?.length ? (
            <PrefillWhenWatcher
                key={`${field.field}-prefillWhen`}
                form={form}
                rules={field.prefillWhen}
                watchPaths={prefillWhenWatchPaths(field.prefillWhen, absoluteName, resolveScopedFieldPath, prefixFor)}
                targetPath={absoluteName}
                resetOnNoMatch={field.prefillWhenReset !== false}
            />
        ) : null;

        // colWhen: live grid width, so a field can shrink when a condition
        // reveals a sibling next to it (mirrors AddFormV1). Inline (combined)
        // cells are flex-sized by combineWidth, so they keep their fixed span.
        const hasColWhen = !opts.inline && colWhenRules(field).length > 0;
        const spanFor = (getFieldValue) => (hasColWhen
            ? resolveColSpan(field, mdSpan, (rule) => getFieldValue(conditionFieldPath(rule, prefixFor(rule.field))))
            : mdSpan);

        if (hasShowIf(field.showIf) || hasColWhen) {
            return (
                <React.Fragment key={field.field}>
                    {prefillWatcher}
                    <Form.Item shouldUpdate noStyle>
                        {({ getFieldValue }) =>
                            (!hasShowIf(field.showIf) || evaluateShowIf(field.showIf, getFieldValue, prefixFor))
                                ? <Col xs={24} md={spanFor(getFieldValue)} style={inlineStyle} className={opts.inline ? 'v1-combine-cell' : undefined}>{fieldItemWithOverride}</Col>
                                : null
                        }
                    </Form.Item>
                </React.Fragment>
            );
        }

        return (
            <React.Fragment key={field.field}>
                {prefillWatcher}
                <Col xs={24} md={mdSpan} style={inlineStyle} className={opts.inline ? 'v1-combine-cell' : undefined}>
                    {fieldItemWithOverride}
                </Col>
            </React.Fragment>
        );
    };

    // renderAddRowField — field-level "addRow": repeats a SINGLE field's control
    // with the same add/remove + min/initial/max config the group-level addRow
    // uses (Form.List), but each list item IS the field's value directly (no
    // sub-key row object). Config keys mirror the group ones: field.minRows /
    // field.initialRows / field.maxRows / field.addLabel.
    //   • minRows    — delete is disabled once the list reaches this count.
    //   • initialRows— empty inputs when the record has no stored values.
    //   • maxRows    — the "Add" button hides once the list reaches this count.
    // Each stored value prefills its own item (buildRepeatFieldInitial reads the
    // field's stored array), and each item renders through singleFieldOf(field)
    // so a repeatable select shows a SINGLE control per item, not a multi-select.
    // Only used for a top-level field (namePrefix === undefined) — a field with
    // addRow=true inside an addRow GROUP renders as a normal single-value field
    // (no nested repeats).
    const renderAddRowField = (field) => {
        const fieldParts = typeof field.field === 'string' && field.field.includes('.')
            ? field.field.split('.')
            : [field.field];
        const permissionLocked = Boolean(field.editablePermission)
            && !roleAllowsAction(field.editablePermission, moduleName);
        const fieldLabel = <FieldLabel field={field} label={field.label} locked={permissionLocked} />;
        const itemField = singleFieldOf(field);
        // subFields → repeat a GROUP of fields per row (each row is an object of
        // the sub-fields, e.g. question + answer) instead of a single scalar.
        // Rows render through the same renderFields the group-level addRow uses.
        const hasSubFields = Array.isArray(field.subFields) && field.subFields.length > 0;
        // Sub-fields are always shown + editable (the admin exposes no show/edit
        // toggle for them). Force both so a sub-field never renders disabled: the
        // Go FormGroupField serializes show/edit as 0 when unset (no omitempty),
        // which editable() would otherwise read as "not editable".
        const subFieldsToRender = hasSubFields
            ? field.subFields.map((sf) => ({ ...sf, show: 1, edit: 1 }))
            : null;
        const minRows = Math.max(0, Number(field.minRows ?? 0) || 0);
        const maxRows = field.maxRows !== undefined && field.maxRows !== '' && Number(field.maxRows) > 0
            ? Number(field.maxRows) : 0;
        // field.value is the stored array (embedded by getFormGroups with an id).
        // The Form.List mounts once groups are populated, so this is correct at mount.
        const initialValue = buildRepeatFieldInitial(field, field.value);

        return (
            <Col key={field.field} xs={24} md={24}>
                <Form.Item label={fieldLabel} required={isFieldRequired(field)} colon={false} className="v1-combine-field">
                    <Form.List
                        name={fieldParts}
                        initialValue={initialValue}
                        rules={[{
                            validator: async (_, items = []) => {
                                if (isFieldRequired(field)) {
                                    const filled = hasSubFields
                                        ? items.some((v) => hasMeaningfulValue(v))
                                        : items.some((v) => v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && v.length === 0));
                                    if (!filled) throw new Error('Mandatory Field');
                                }
                                if (minRows > 0 && items.length < minRows) {
                                    throw new Error(`Keep at least ${minRows} ${hasSubFields ? 'row' : 'value'}${minRows > 1 ? 's' : ''}.`);
                                }
                            },
                        }]}
                    >
                        {(items, { add, remove }, { errors }) => (
                            <div className="v1-addrow-wrapper">
                                {items.map(({ key: itemKey, name: itemName }) => (
                                    <div key={itemKey} className="v1-addrow-item">
                                        <Button
                                            type="text" danger icon={<DeleteOutlined />}
                                            onClick={() => remove(itemName)}
                                            disabled={items.length <= minRows}
                                            className="v1-addrow-delete-btn"
                                        />
                                        <div className="v1-addrow-fields">
                                            {hasSubFields ? (
                                                <Row gutter={[24, 0]} align="top">
                                                    {renderFields(subFieldsToRender, itemName, fieldParts[0], false, field.field)}
                                                </Row>
                                            ) : (
                                                <Form.Item
                                                    name={[itemName]}
                                                    noStyle
                                                    valuePropName={field.type === 'checkbox' && !(field.options?.length) ? 'checked' : 'value'}
                                                    getValueFromEvent={field.type === 'file' ? (e) => (Array.isArray(e) ? e : e?.fileList) : undefined}
                                                    rules={getRules(itemField, form, [...fieldParts, itemName], false)}
                                                >
                                                    <FieldControl
                                                        field={itemField}
                                                        form={form}
                                                        name={[...fieldParts, itemName]}
                                                        moduleName={moduleName}
                                                        recordId={recordId}
                                                        scopeValues={{ clientId }}
                                                        formDisabled={aiActions.busy}
                    onRegionBlocked={handleRegionBlocked}
                    onRowOrderChange={handleRowOrder}
                                                    />
                                                </Form.Item>
                                            )}
                                        </div>
                                    </div>
                                ))}
                                {(!maxRows || items.length < maxRows) && (
                                    <Button type="link" icon={<PlusOutlined />} onClick={() => add()} className="v1-addrow-add-btn">
                                        {field.addLabel ?? `Add ${field.label ?? 'Value'}`}
                                    </Button>
                                )}
                                <Form.ErrorList errors={errors} />
                            </div>
                        )}
                    </Form.List>
                </Form.Item>
            </Col>
        );
    };

    // renderNestedRepeatField — a repeat field (field.addRow) inside an addRow
    // group row. Nests its own Form.List under the current row so every row owns
    // independent repeat values (group[rowIndex].repeatField[repeatIndex]). Also
    // handles field.subFields (repeat a GROUP of sub-fields together per row,
    // e.g. question + answer) nested the same way — each item renders the
    // sub-fields through the same renderFields the top-level subFields branch
    // (renderAddRowField) uses, so a select/lookup sub-field gets identical
    // config-driven dropdown loading. namePrefix = the row index (from the
    // group Form.List); listName = the group's Form.List name (string), or a
    // doubly-nested list's absolute path array. Mirrors AddFormV1.
    const renderNestedRepeatField = (field, namePrefix, listName) => {
        const fieldParts = typeof field.field === 'string' && field.field.includes('.')
            ? field.field.split('.')
            : [field.field];
        const permissionLocked = Boolean(field.editablePermission)
            && !roleAllowsAction(field.editablePermission, moduleName);
        const fieldLabel = <FieldLabel field={field} label={field.label} locked={permissionLocked} />;
        const itemField = singleFieldOf(field);
        const hasSubFields = Array.isArray(field.subFields) && field.subFields.length > 0;
        const subFieldsToRender = hasSubFields
            ? field.subFields.map((sf) => ({ ...sf, show: 1, edit: 1 }))
            : null;
        const minRows = Math.max(0, Number(field.minRows ?? 0) || 0);
        const maxRows = field.maxRows !== undefined && field.maxRows !== '' && Number(field.maxRows) > 0
            ? Number(field.maxRows) : 0;
        const listNameRel = [namePrefix, ...fieldParts];
        const nestedAbsoluteListName = [...(Array.isArray(listName) ? listName : [listName]), namePrefix, ...fieldParts];
        // Empty starter inputs for a freshly-added row; an edited row inherits its
        // stored values from the parent row's initialValue (antd ignores this when
        // the store already holds a value at this path).
        const initialValue = buildRepeatFieldInitial(field, undefined);
        return (
            <Col key={`${listName}-${namePrefix}-${field.field}`} xs={24} md={hasSubFields ? 24 : (field.col ?? 24)}>
                <Form.Item label={fieldLabel} required={isFieldRequired(field)} colon={false} className="v1-combine-field">
                    <Form.List
                        name={listNameRel}
                        initialValue={initialValue}
                        rules={hasSubFields ? [{
                            validator: async (_, items = []) => {
                                if (isFieldRequired(field)) {
                                    const filled = items.some((v) => hasMeaningfulValue(v));
                                    if (!filled) throw new Error('Mandatory Field');
                                }
                                if (minRows > 0 && items.length < minRows) {
                                    throw new Error(`Keep at least ${minRows} row${minRows > 1 ? 's' : ''}.`);
                                }
                            },
                        }] : []}
                    >
                        {(items, { add, remove }, { errors }) => (
                            <div className="v1-addrow-wrapper">
                                {items.map(({ key: itemKey, name: itemName }) => {
                                    const absoluteName = [...nestedAbsoluteListName, itemName];
                                    return (
                                        <div key={itemKey} className="v1-addrow-item">
                                            <Button
                                                type="text" danger icon={<DeleteOutlined />}
                                                onClick={() => remove(itemName)}
                                                disabled={items.length <= minRows}
                                                className="v1-addrow-delete-btn"
                                            />
                                            <div className="v1-addrow-fields">
                                                {hasSubFields ? (
                                                    <Row gutter={[24, 0]} align="top">
                                                        {renderFields(subFieldsToRender, itemName, nestedAbsoluteListName, false, field.field)}
                                                    </Row>
                                                ) : (
                                                    <Form.Item
                                                        name={[itemName]}
                                                        noStyle
                                                        valuePropName={field.type === 'checkbox' && !(field.options?.length) ? 'checked' : 'value'}
                                                        getValueFromEvent={field.type === 'file' ? (e) => (Array.isArray(e) ? e : e?.fileList) : undefined}
                                                        rules={getRules(itemField, form, absoluteName, false)}
                                                    >
                                                        <FieldControl
                                                            field={itemField}
                                                            form={form}
                                                            name={absoluteName}
                                                            moduleName={moduleName}
                                                            recordId={recordId}
                                                            scopeValues={{ clientId }}
                                                            formDisabled={aiActions.busy}
                    onRegionBlocked={handleRegionBlocked}
                    onRowOrderChange={handleRowOrder}
                                                        />
                                                    </Form.Item>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                                {(!maxRows || items.length < maxRows) && (
                                    <Button type="link" icon={<PlusOutlined />} onClick={() => add()} className="v1-addrow-add-btn">
                                        {field.addLabel ?? `Add ${field.label ?? 'Value'}`}
                                    </Button>
                                )}
                                <Form.ErrorList errors={errors} />
                            </div>
                        )}
                    </Form.List>
                </Form.Item>
            </Col>
        );
    };


// registerHiddenFields — a show:0 field still needs a Form.Item.
//
// A hidden field is not decoration: it is a CARRIER. The engine deliberately
// keeps show:0 fields in the group (see normalizeGroups) precisely so their
// values participate — the job's client rate rides onto a submission that way
// (jobClientRate.*, filled by prefillFromModule), and a verify result rides on
// isValid/validatedAt.
//
// But a value set with setFieldsValue on a path NOTHING has registered is not
// reliably part of the form: onFinish drops it, and a cross-field rule reading
// it through getFieldValue can come back empty. That is why the verify carriers
// were already registered by hand at their own call site. Doing it here covers
// EVERY config-declared hidden field instead of the two someone remembered,
// which is what makes a rule like "rate may not exceed the job's client rate"
// actually fire — before this it silently found no ceiling and passed anything.
//
// Rendered `hidden` (never visible), unstyled, and outside the layout grid, so
// nothing about the form's appearance changes.
const renderHiddenCarriers = (fields, namePrefix) => fields
    .filter((f) => f.field && f.show !== undefined && !truthy(f.show))
    .map((f) => {
        const parts = String(f.field).includes('.') ? String(f.field).split('.') : f.field;
        const name = namePrefix !== undefined
            ? [namePrefix, ...(Array.isArray(parts) ? parts : [parts])]
            : parts;
        return (
            <Form.Item key={`hidden-${f.field}`} name={name} hidden noStyle>
                <Input />
            </Form.Item>
        );
    });

    const renderFields = (fields, namePrefix, listName, hideLabel = false, groupNameForActions) => {
        const used = new Set();
        const out = [];
        // Keys of THIS group's own fields — conditions (showIf/labelWhen/
        // prefillWhen) referencing a field OUTSIDE the group (e.g. the top-level
        // contractType from inside the addRow employer group) must resolve
        // against the top-level form, not the row.
        const rowFieldKeys = new Set(fields.map((f) => f.field).filter(Boolean));
        // Hidden (show:0) fields stay in `fields` so buildPayload can see them
        // (see normalizeGroups) — they just never get a Form.Item here.
        const visibleFields = fields.filter((f) => f.show === undefined || truthy(f.show));
        out.push(...renderHiddenCarriers(fields, namePrefix));
        visibleFields.forEach((field) => {
            if (truthy(field.addRow) && namePrefix === undefined) {
                out.push(renderAddRowField(field));
                return;
            }
            // A repeat field INSIDE an addRow group row (namePrefix defined) nests
            // its own Form.List so each row keeps independent repeat values
            // (educationDetails[rowIndex].subjects[]) — including a subFields
            // repeat-GROUP (e.g. question+answer pairs per education row).
            if (truthy(field.addRow) && namePrefix !== undefined) {
                out.push(renderNestedRepeatField(field, namePrefix, listName));
                return;
            }
            const combineKey = field.combineKey?.trim?.();
            if (!combineKey) {
                out.push(renderField(field, namePrefix, listName, { hideLabel, groupNameForActions, rowFieldKeys }));
                return;
            }
            const packKey = `${listName ?? 'root'}:${namePrefix ?? 'root'}:${combineKey}`;
            if (used.has(packKey)) return;
            const pack = visibleFields.filter((f) => f.combineKey === combineKey);
            pack.forEach((f) => used.add(`${listName ?? 'root'}:${namePrefix ?? 'root'}:${f.combineKey}`));

            const labelField = pack.find((f) => f.combineLabel) ?? field;
            const siblingPrefix = (listName !== undefined && namePrefix !== undefined)
                ? [...(Array.isArray(listName) ? listName : [listName]), namePrefix]
                : null;
            // A combined pack prints ONE shared label; an override checkbox on any
            // member belongs beside it (inline members are label-less by design).
            const packOverrideField = pack.find((f) => overrideConfig(f)) ?? null;
            const packPrefixForOverride = (conditionKey) => (
                siblingPrefix && (!rowFieldKeys || rowFieldKeys.has(conditionKey)) ? siblingPrefix : null
            );
            const packOverrideAbsName = packOverrideField
                ? ((listName !== undefined && namePrefix !== undefined)
                    ? [...(Array.isArray(listName) ? listName : [listName]), namePrefix,
                        ...(String(packOverrideField.field).includes('.')
                            ? String(packOverrideField.field).split('.')
                            : [packOverrideField.field])]
                    : (String(packOverrideField.field).includes('.')
                        ? String(packOverrideField.field).split('.')
                        : packOverrideField.field))
                : null;
            const baseCombinedLabel = <ConfiguredFieldLabel form={form} field={labelField} siblingPrefix={siblingPrefix} combined />;
            const label = packOverrideField ? (
                <span className="v1-label-with-override">
                    {baseCombinedLabel}
                    <ValidationOverrideCheckbox
                        form={form}
                        field={packOverrideField}
                        flagPath={overrideFlagPath(packOverrideField, packOverrideAbsName, packPrefixForOverride)}
                        fieldPath={packOverrideAbsName}
                    />
                </span>
            ) : baseCombinedLabel;
            const toggleTarget = pack.find((f) => f.combineToggleTarget)?.combineToggleTarget;
            const toggleLabel = pack.find((f) => f.combineToggleLabel)?.combineToggleLabel ?? 'Show range';
            const wrapPack = pack.some((f) => truthy(f.combineWrap));
            const configuredSpan = Number(pack.find((f) => [12, 24].includes(Number(f.combineSpan)))?.combineSpan);
            const targetField = toggleTarget ? pack.find((f) => fieldKey(f) === toggleTarget) : null;
            const targetParts = targetField?.field?.includes?.('.') ? targetField.field.split('.') : [targetField?.field].filter(Boolean);
            const targetName = targetField
                ? ((listName !== undefined && namePrefix !== undefined)
                    ? [listName, namePrefix, ...targetParts]
                    : (targetParts.length > 1 ? targetParts : targetField.field))
                : null;

            // One label, boxes inline on a single row, range toggle BELOW. Toggle off →
            // only the primary (target hidden); on → the target box appears alongside.
            const primaryReq = pack.some((f) => isFieldRequired(f));
            const comboSpan = [12, 24].includes(configuredSpan)
                ? configuredSpan
                : (pack.length >= 3 ? 24 : 12);
            // A combined pack prints ONE shared label above its boxes, outside the
            // per-field showIf gates — so when every member is hidden the boxes
            // vanish but the labelled shell stayed (e.g. an empty "Contact Number"
            // on a W2 employer). Gate the pack on "is any member still visible".
            const packPrefixFor = (conditionKey) => (
                siblingPrefix && (!rowFieldKeys || rowFieldKeys.has(conditionKey)) ? siblingPrefix : null
            );
            const packHasShowIf = pack.some((f) => hasShowIf(f.showIf));
            const anyPackMemberVisible = (getFieldValue) => pack.some(
                (f) => !hasShowIf(f.showIf) || evaluateShowIf(f.showIf, getFieldValue, packPrefixFor),
            );
            const packCol = (
                <Col key={packKey} xs={24} md={comboSpan}>
                    <Form.Item label={label} required={primaryReq} colon={false} className="v1-combine-field">
                        <Form.Item noStyle shouldUpdate>
                            {({ getFieldValue }) => {
                                const hasTargetValue = targetName ? !empty(getFieldValue(targetName)) : false;
                                const checked = combineToggles[packKey] ?? hasTargetValue;
                                const visiblePack = toggleTarget && !checked
                                    ? pack.filter((f) => fieldKey(f) !== toggleTarget)
                                    : pack;
                                return (
                                    <>
                                        <Row
                                            gutter={[8, 0]}
                                            align="top"
                                            wrap={wrapPack}
                                            className={`v1-combine-row${wrapPack ? ' v1-combine-row-wrap' : ''}`}
                                        >
                                            {/* Combined-field mandatory cascade (mirrors AddFormV1):
                                                any required field in a combined pack makes every
                                                visible field in the pack required. */}
                                            {visiblePack.map((f) => renderField(primaryReq ? { ...f, req: 1 } : f, namePrefix, listName, { inline: true }))}
                                        </Row>
                                        {toggleTarget && (
                                            <div className="v1-combine-range">
                                                <Switch
                                                    size="small"
                                                    checked={checked}
                                                    onChange={(next) => {
                                                        if (!next && targetName) {
                                                            form.setFieldValue(targetName, undefined);
                                                            form.validateFields([targetName]).catch(() => { });
                                                        }
                                                        setCombineToggles((prev) => ({ ...prev, [packKey]: next }));
                                                    }}
                                                />
                                                <Text type="secondary" className="v1-combine-toggle-label">{toggleLabel}</Text>
                                            </div>
                                        )}
                                    </>
                                );
                            }}
                        </Form.Item>
                    </Form.Item>
                </Col>
            );
            out.push(packHasShowIf ? (
                <Form.Item key={packKey} shouldUpdate noStyle>
                    {({ getFieldValue }) => (anyPackMemberVisible(getFieldValue) ? packCol : null)}
                </Form.Item>
            ) : packCol);
        });
        return out;
    };

    // renderGroupBody — a group's inner controls without its Card chrome, so a
    // `mergeInto` child group (e.g. Rate Details) can render inside another
    // group's card. Mirrors AddFormV1: presentation only, payload untouched.
    const renderGroupBody = (group) => {
        const groupName = group.name ?? group.id ?? '';
        // group.linkGroup: when this group is linked to others, the LEADER owns
        // the Add button + minRows/requireOne validator for the whole set; every
        // member (leader + followers) still gets its own Form.List/Card/rows, and
        // add/remove always go through every member via listControlsRef so row
        // counts stay equal by construction. Unlinked groups (linkedSet === null,
        // i.e. every group that predates this feature) fall through leaderGroup
        // === group, so minRows/rules/initialValue below compute exactly as before.
        const linkedSet = findLinkedSet(linkedGroupSets, groupName);
        const isFollower = Boolean(linkedSet) && linkedSet.leaderName !== groupName;
        const leaderGroup = linkedSet
            ? (groups.find((g) => g.name === linkedSet.leaderName) ?? group)
            : group;
        const minRows = Math.max(0, Number(leaderGroup.minRows ?? 0) || 0);
        const maxRows = Math.max(0, Number(leaderGroup.maxRows ?? 0) || 0);
        const linkedMemberNames = linkedSet ? linkedSet.memberNames : [groupName];
        const addLinkedRow = () => {
            // New rows start from the group's admin defaults (defaultOnEdit).
            linkedMemberNames.forEach((name) => {
                const member = groups.find((g) => g.name === name) ?? group;
                listControlsRef.current[name]?.add(rowDefaultsFor(member, { editing: true }));
            });
        };
        const removeLinkedRow = (rowName) => {
            linkedMemberNames.forEach((name) => listControlsRef.current[name]?.remove(rowName));
        };

        const body = group.addRow ? (
            <Form.List
                name={groupName}
                initialValue={addRowInitials[group.name] ?? []}
                rules={isFollower ? [] : [
                    {
                        validator: async (_, rows = []) => {
                            // A group switched OFF via its own toggle (e.g. "Fresher" hiding
                            // Work Experience) is not rendered at all — don't force rows on it.
                            const switchedOn = truthy(group.toggleEnabled) && group.toggleField
                                && Boolean(form.getFieldValue(group.toggleField));
                            if (switchedOn) return;
                            if (minRows > 0 && rows.length < minRows) {
                                throw new Error(`Keep at least ${minRows} row${minRows > 1 ? 's' : ''}.`);
                            }
                            if (truthy(group.requireOne)) {
                                const invalid = rows.some((row) => !hasMeaningfulValue(row));
                                if (invalid) throw new Error('Fill at least one field in each added row.');
                            }
                        },
                    },
                ]}
            >
                {(rows, { add, remove, move }, { errors }) => {
                    // `move` joins the registry so the newest-first prompt can
                    // reorder a row the user just marked as ongoing. Form.List's
                    // own move preserves every row's values; rebuilding the
                    // array by hand would not.
                    listControlsRef.current[groupName] = { add, remove, move };
                    return (
                        <div className="v1-addrow-wrapper">
                            <div className="v1-addrow-topbar">
                                {!isFollower ? (
                                    (!maxRows || rows.length < maxRows) && (
                                        <Button
                                            type="link"
                                            icon={<PlusOutlined />}
                                            onClick={addLinkedRow}
                                            className="v1-addrow-add-btn"
                                        >
                                            {group.addLabel ?? `Add ${group.label ?? 'Row'}`}
                                        </Button>
                                    )
                                ) : (
                                    <Text type="secondary" className="v1-addrow-linked-hint">
                                        Rows added via {leaderGroup.label ?? leaderGroup.name}
                                    </Text>
                                )}
                            </div>

                            {truthy(group.sharedRowLabels) && rows.length > 0 && (
                                <Row gutter={[24, 0]} className="v1-addrow-header">
                                    {group.fields.filter((f) => f.show === undefined || truthy(f.show)).map((field) => (
                                        <Col key={fieldKey(field)} md={field.col ?? 12} className="v1-addrow-header-label">
                                            {field.label}
                                        </Col>
                                    ))}
                                </Row>
                            )}

                            {rows.map(({ key: rowKey, name }) => (
                                <div key={rowKey} className="v1-addrow-item">
                                    <Button
                                        type="text"
                                        danger
                                        icon={<DeleteOutlined />}
                                        disabled={rows.length <= minRows}
                                        onClick={() => removeLinkedRow(name)}
                                        className="v1-addrow-delete-btn"
                                    />
                                    <div className="v1-addrow-fields">
                                        <Row gutter={[24, 0]} align="top">
                                            {renderFields(group.fields, name, groupName, truthy(group.sharedRowLabels), groupName)}
                                        </Row>
                                    </div>
                                </div>
                            ))}
                            <Form.ErrorList errors={errors} />
                        </div>
                    );
                }}
            </Form.List>
        ) : (
            <Row gutter={[24, 0]}>{renderFields(group.fields, undefined, undefined, false, groupName)}</Row>
        );

        return body;
    };

    // Groups rendered INSIDE another group's card (group.mergeInto) — see
    // AddFormV1 for the rationale; identical here so add and edit match.
    const mergedChildrenOf = (group) => groups.filter(
        (g) => g !== group && g.mergeInto && String(g.mergeInto) === String(group.name),
    );
    const isMergedChild = (group) => Boolean(
        group.mergeInto && groups.some((g) => g !== group && String(g.name) === String(group.mergeInto)),
    );

    // A merged child renders BEFORE the host's own fields when it declares
    // `mergePosition: "top"`. Needed whenever the child holds the field the
    // host's fields are conditioned on — Contract Type gates every Employer
    // field, so rendering it underneath them reads backwards and invites the
    // user to fill a disabled card first.
    const mergedAt = (group, position) => mergedChildrenOf(group).filter(
        (child) => (String(child.mergePosition ?? 'bottom') === position),
    );


    const renderGroup = (group, index) => {
        const key = group.name ?? group.id ?? String(index);
        const body = renderGroupBody(group);
        const mergedTop = mergedAt(group, 'top');
        const mergedChildren = mergedAt(group, 'bottom');

        // One renderer for both slots, so a merged child behaves identically
        // whichever side of the host body it sits on.
        const renderMergedChild = (child) => {
            const section = (
                <div key={child.name} className="v1-merged-group">
                    <div className="v1-merged-group-title">{child.label ?? child.name}</div>
                    {renderGroupBody(child)}
                </div>
            );
            const gated = child.showIf?.field
                ? <GroupShowIfGate key={child.name} form={form} showIf={child.showIf} render={() => section} />
                : section;
            // Its OWN disabled state, not the host's — see GroupDisabledProvider.
            return (
                <GroupDisabledProvider key={child.name} form={form} group={child} inheritParent={false}>
                    {gated}
                </GroupDisabledProvider>
            );
        };

        return (
            <Card
                key={key}
                className="v1-card v1-edit-card"
                title={
                    <SectionTitle
                        icon={sectionIcons[index % sectionIcons.length]}
                        title={group.label ?? group.name ?? 'Details'}
                    />
                }
                extra={
                    <Space size={8} align="center" className="v1-group-actions">
                        <GroupInfoMessage group={group} />
                        <GroupToggleControl group={group} />
                        {/* Opt-in only (group.allowCollapse) — see AddFormV1. */}
                        {truthy(group.allowCollapse) && (
                            <Button type="text" size="small"
                                aria-expanded={!collapsed[key]}
                                aria-label={collapsed[key] ? 'Expand section' : 'Collapse section'}
                                icon={collapsed[key] ? <DownOutlined /> : <UpOutlined />}
                                onClick={(e) => { e.stopPropagation(); toggle(key); }}
                            />
                        )}
                    </Space>
                }
            >
                <Form.Item noStyle shouldUpdate>
                    {({ getFieldValue }) => {
                        const switchedOn = truthy(group.toggleEnabled) && group.toggleField && Boolean(getFieldValue(group.toggleField));
                        return (
                            <div style={{ display: collapsed[key] || switchedOn ? 'none' : 'block' }}>
                                {mergedTop.map(renderMergedChild)}
                                {body}
                                {mergedChildren.map(renderMergedChild)}
                            </div>
                        );
                    }}
                </Form.Item>
            </Card>
        );
    };

    return (
        <main>
            {resolvedBreadcrumbs.length > 0 && <AppBreadcrumb items={resolvedBreadcrumbs} />}

            <div className="v1-edit-page">
                <div className="v1-page-inner">
                    {/* Same split as the Add form: async work gets a
                        viewport-centred overlay, because a wrapping Spin
                        centres inside the element it wraps and on a long form
                        that is off screen after any scrolling — a frozen form
                        with no sign anything is happening. */}
                    <Spin
                        fullscreen
                        spinning={aiActions.busy}
                        size="large"
                        tip={aiActions.loadingText || 'Reading the file…'}
                    />
                    <Spin spinning={loading} tip="Loading form fields…">
                        {error && (
                            <Alert type="error" showIcon message={error}
                                style={{ marginBottom: 16 }} />
                        )}
                        {submitError && (
                            <Alert type="error" showIcon message={submitError}
                                style={{ marginBottom: 16 }} />
                        )}

                        <Form
                            form={form}
                            layout="vertical"
                            colon={false}
                            className="v1-form"
                            disabled={aiActions.busy}
                            requiredMark={(label, { required }) => (
                                <>
                                    {label}
                                    {required && <span className="v1-required-mark"> *</span>}
                                </>
                            )}
                            onFinish={submit}
                            onFinishFailed={handleSubmitFailed}
                        >
                            {groups.filter((g) => !isMergedChild(g)).map((group, index) => (
                                <GroupDisabledProvider key={group.name ?? `grp-${index}`} form={form} group={group}>
                                    {group.showIf?.field
                                        ? <GroupShowIfGate form={form} showIf={group.showIf} render={() => renderGroup(group, index)} />
                                        : renderGroup(group, index)}
                                </GroupDisabledProvider>
                            ))}

                            {!loading && !error && groups.length === 0 && (
                                <Alert type="info" showIcon message="No form fields are configured." />
                            )}
                        </Form>
                    </Spin>
                </div>

                <div className={embedded ? 'v1-footer v1-footer--embedded' : 'v1-footer'}>
                    <Text type="secondary">
                        All unsaved changes will be lost if you cancel and reload.
                    </Text>
                    <Space size={12}>
                        <Button onClick={() => (embedded && onCancel
                            ? onCancel()
                            : goBackOrTo(navigate, { cancelPath }))}>
                            Cancel
                        </Button>
                        <ValidationGatedSubmitButton
                            form={form}
                            groups={groups}
                            onClick={() => form.submit()}
                            disabled={loading || submitting || uniquePending > 0}
                            loading={submitting}
                        >
                            {submitText}
                        </ValidationGatedSubmitButton>
                    </Space>
                </div>
            </div>
        </main>
    );
}
